import React, { useCallback, useEffect, useMemo, useState, memo } from 'react';
// src/components/grid/StaffAllocationGrid.tsx
// FIXED: Memoized component to prevent unnecessary re-renders
// Using formatClassDisplayName utility for consistent formatting
import { StaffAllocationGrid as StaffGrid, ClassAllocation } from '../../types/grid';
import { Staff, useData } from '../../contexts/DataContext';
import { useMultiGrid } from '../../contexts/MultiGridContext';
import { DEFAULT_MINUTES_PER_LINE } from '../../types/schoolConfig';
import TeacherAssignmentModal from './TeacherAssignmentModal';
import LineManagementModal from './LineManagementModal';
import CreateCompositeClassModal from './CreateCompositeClassModal';
import CreateClassModal from './CreateClassModal';
import { Search, Edit2, Check, X, Filter, Settings, Users, ChevronDown, Flag, Layers, Trash2, Plus } from 'lucide-react';
import { TeachingIntention } from '../../types/intentions';
import { CurriculumSubject } from '../../utils/preferenceMatching';
import { formatClassDisplayName, extractClassLevel } from '../../utils/classDisplayUtils';
import { SubjectLineAvailability, isSubjectAvailableOnLine } from '../../types/lineSubject';
import { CellFlag, FLAG_COLORS, getFlagColorConfig, isCellFlagged, createFlagId } from '../../types/flags';
import { gridsService } from '../../services/gridsService';
import { displayLearningArea } from '../../constants/learningAreas';
import { debug } from '../../utils/debug';
import EmptyState from '../ui/EmptyState';
import { useConfirm } from '../../contexts/ConfirmContext';
import { useToast } from '../../contexts/ToastContext';
// Remembers the group-by-subject view choice between visits.
const GROUP_BY_SUBJECT_KEY = 'pit_grid_group_by_subject';

// Minimum height of a single assignment slot. A cell holding N parallel classes
// is N slots tall, which is what makes the row grow rather than squashing them.
const ASSIGNMENT_SLOT_MIN_HEIGHT = 56;

/**
 * One rendered row of the editor: either a single class (ungrouped), or every
 * class sharing a subject name (grouped).
 */
interface DisplayRow {
  key: string;
  label: string;
  curriculumArea?: string;
  classes: ClassAllocation[];
  /** True when this row represents several classes collapsed together. */
  isGroup: boolean;
}

/**
 * Whether a class occupies a line, and so should get an assignment slot in that
 * column of a grouped row.
 *
 * Per-class signals win outright. The line_subject_config availability map is
 * keyed by SUBJECT (code or name), not by class, so every class of a subject
 * matches every line that subject may run on — fine for the ungrouped view,
 * where it only tints one cell, but catastrophic here: on the 2026 grid it gave
 * DISCOVER - MATHS all 27 of its classes a slot on all 12 lines, a 27-slot-tall
 * row. So the map is consulted only for a class that has no placement of its own
 * yet, which is exactly the case it exists to cover.
 */
function classOccupiesLine(
  classItem: ClassAllocation,
  lineId: string,
  subjectLineAvailability?: SubjectLineAvailability
): boolean {
  const hasOwnPlacement =
    Object.keys(classItem.lineAssignments || {}).length > 0 ||
    (classItem.allowedLineIds?.length || 0) > 0;

  if (hasOwnPlacement) {
    return (
      (classItem.lineAssignments && lineId in classItem.lineAssignments) ||
      !!classItem.allowedLineIds?.includes(lineId)
    );
  }

  const subjectKey = classItem.subjectCode || classItem.className || classItem.id;
  return isSubjectAvailableOnLine(subjectKey, lineId, subjectLineAvailability);
}

interface StaffAllocationGridProps {
  grid: StaffGrid;
  staff: Staff[];
  onAssignTeacher: (classId: string, lineId: string, teacherCode: string | null) => void;
  onClassUpdate?: (classId: string, updates: Partial<ClassAllocation>) => void;
  onLineUpdate?: (lineId: string, newName: string) => void;
  onGridUpdate?: (gridUpdates: Partial<StaffGrid>) => Promise<void>;
  // New props for preference matching
  teachingIntentions?: TeachingIntention[];
  curriculumSubjects?: CurriculumSubject[];
  // Preference loading props
  preferencesLoading?: boolean;
  onLoadPreferences?: () => Promise<void>;
  // Line-subject availability for color coding
  subjectLineAvailability?: SubjectLineAvailability;
  // Staff filtering
  staffFilter?: string[];
  onStaffFilterChange?: (staffFilter: string[]) => void;
  // Semester filtering
  activeSemester?: 'both' | 'semester1' | 'semester2';
  // Cell flagging
  flags?: CellFlag[];
  onFlagCell?: (flag: Omit<CellFlag, 'id' | 'createdAt' | 'createdBy'>) => void;
  onRemoveFlag?: (classId: string, lineId: string) => void;
}
// ✅ FIXED: Memoized main component
const StaffAllocationGrid: React.FC<StaffAllocationGridProps> = memo(({
  grid,
  staff,
  onAssignTeacher,
  onClassUpdate,
  onLineUpdate,
  onGridUpdate,
  teachingIntentions = [],
  curriculumSubjects = [],
  preferencesLoading = false,
  onLoadPreferences,
  subjectLineAvailability = {},
  staffFilter = [],
  onStaffFilterChange,
  activeSemester = 'both',
  flags = [],
  onFlagCell,
  onRemoveFlag
}) => {
  // Access helper functions for staff code hierarchy
  const { findStaffByCode, getStaffDisplayCode } = useData();
  const confirm = useConfirm();
  const { addToast } = useToast();

  // Access all grids to get Main Grid's curriculum areas
  const { allGrids } = useMultiGrid();
  
  // Calculate staff members who are actually assigned to classes in this grid
  const assignedStaffMembers = useMemo(() => {
    const assignedStaffCodes = new Set<string>();

    // Collect all teacher codes from line assignments in the grid.
    // Guard against classes with no lineAssignments (undefined/null) - a bare
    // Object.entries(undefined) throws and blanks the whole grid editor.
    (grid.classes ?? []).forEach(classItem => {
      Object.entries(classItem.lineAssignments ?? {}).forEach(([lineId, assignment]) => {
        if (assignment) {
          // Handle single teacher assignments (string)
          if (typeof assignment === 'string') {
            // Check if it's a JSON string (multi-teacher assignment)
            if (assignment.startsWith('[') && assignment.endsWith(']')) {
              try {
                const parsedAssignment = JSON.parse(assignment);
                if (Array.isArray(parsedAssignment)) {
                  parsedAssignment.forEach(teacher => {
                    if (teacher && teacher.teacherCode) {
                      assignedStaffCodes.add(teacher.teacherCode);
                    }
                  });
                } else {
                  assignedStaffCodes.add(assignment);
                    }
              } catch (e) {
                assignedStaffCodes.add(assignment);
              }
            } else {
              assignedStaffCodes.add(assignment);
            }
          }
          // Handle multi-teacher assignments (array)
          else if (Array.isArray(assignment)) {
            assignment.forEach(teacher => {
              if (typeof teacher === 'string') {
                assignedStaffCodes.add(teacher);
              } else if (teacher && typeof teacher === 'object' && teacher.teacherCode) {
                assignedStaffCodes.add(teacher.teacherCode);
              }
            });
          }
          // Handle assignment objects
          else if (assignment && typeof assignment === 'object') {
            if (assignment.teacherCode) {
              assignedStaffCodes.add(assignment.teacherCode);
            }
            // Handle multi-teacher object format
            if (assignment.teachers && Array.isArray(assignment.teachers)) {
              assignment.teachers.forEach(teacher => {
                if (teacher && teacher.teacherCode) {
                  assignedStaffCodes.add(teacher.teacherCode);
                }
              });
            }
          }
        }
      });
    });

    // Enhanced staff matching logic to handle code mismatches
    const matchedStaff = staff.filter(staffMember => {
      const currentDisplayCode = getStaffDisplayCode(staffMember);

      // 1. Direct match with current display code (staffCode || initial)
      if (assignedStaffCodes.has(currentDisplayCode)) {
        return true;
      }

      // 2. Check staffCode individually (in case display logic changed)
      if (staffMember.staffCode && assignedStaffCodes.has(staffMember.staffCode)) {
        return true;
      }

      // 3. Fuzzy matching for legacy compatibility
      for (const assignedCode of assignedStaffCodes) {
        // Handle case where assignment uses 3-char code but staff now has 4-char code with X
        if (assignedCode.length === 3 && currentDisplayCode === assignedCode + 'X') {
          return true;
        }

        // Handle case where assignment has X suffix but current code doesn't
        if (assignedCode.endsWith('X') && assignedCode.slice(0, -1) === currentDisplayCode) {
          return true;
        }

        // Handle code variations with numeric suffixes (e.g., "ACL" vs "ACL_2")
        const baseAssignedCode = assignedCode.replace(/[X_]\d*$/, '');
        const baseDisplayCode = currentDisplayCode.replace(/[X_]\d*$/, '');
        if (baseAssignedCode.length >= 2 && baseAssignedCode === baseDisplayCode) {
          return true;
        }

        // Handle common variations where middle letter might be different
        if (assignedCode.length === 3 && currentDisplayCode.length === 3) {
          const firstLast = (code: string) => code[0] + code[2];
          if (firstLast(assignedCode) === firstLast(currentDisplayCode)) {
            return true;
          }
        }
      }

      return false;
    });

    return matchedStaff;
  }, [grid.classes, staff, getStaffDisplayCode]);

  // Helper function to check if a teacher assignment matches any of the selected staff filter codes
  const isTeacherInStaffFilter = useCallback((teacherCode: string, staffFilter: string[]): boolean => {
    if (!teacherCode || staffFilter.length === 0) return false;

    // Direct match
    if (staffFilter.includes(teacherCode)) {
      return true;
    }

    // Find staff member and check if their display code matches any filter
    const staffMember = staff.find(s =>
      s.staffCode === teacherCode ||
      getStaffDisplayCode(s) === teacherCode
    );

    if (staffMember) {
      const displayCode = getStaffDisplayCode(staffMember);
      if (staffFilter.includes(displayCode)) {
        return true;
      }
      if (staffMember.staffCode && staffFilter.includes(staffMember.staffCode)) {
        return true;
      }
    }

    // Fuzzy matching for legacy compatibility
    for (const filterCode of staffFilter) {
      // Handle case where filter uses display code but assignment uses 3-char code
      if (filterCode.length === 4 && filterCode.endsWith('X') && teacherCode === filterCode.slice(0, -1)) {
        return true;
      }

      // Handle case where filter uses 3-char code but assignment has X suffix
      if (filterCode.length === 3 && teacherCode === filterCode + 'X') {
        return true;
      }

      // Handle code variations with suffixes
      const baseFilterCode = filterCode.replace(/[X_]\d*$/, '');
      const baseTeacherCode = teacherCode.replace(/[X_]\d*$/, '');
      if (baseFilterCode.length >= 2 && baseFilterCode === baseTeacherCode) {
        return true;
      }

      // Handle common variations where middle letter might be different
      if (filterCode.length === 3 && teacherCode.length === 3) {
        const firstLast = (code: string) => code[0] + code[2];
        if (firstLast(filterCode) === firstLast(teacherCode)) {
          return true;
        }
      }
    }

    return false;
  }, [staff, getStaffDisplayCode]);

  const [selectedCurriculumAreas, setSelectedCurriculumAreas] = useState<string[]>([]);
  const [isAreaDropdownOpen, setIsAreaDropdownOpen] = useState(false);
  const [areaSearchQuery, setAreaSearchQuery] = useState('');
  const [selectedSemester, setSelectedSemester] = useState<string>('fullyear');
  const [selectedLevels, setSelectedLevels] = useState<string[]>([]);
  const [isLevelDropdownOpen, setIsLevelDropdownOpen] = useState(false);
  const [levelSearchQuery, setLevelSearchQuery] = useState('');
  const [selectedCell, setSelectedCell] = useState<{
    classId: string;
    lineId: string;
  } | null>(null);
  const [isCommentsModalOpen, setIsCommentsModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [editingLineId, setEditingLineId] = useState<string | null>(null);
  const [editingLineName, setEditingLineName] = useState<string>('');
  const [showLineManagement, setShowLineManagement] = useState<boolean>(false);
  const [showStaffFilterDropdown, setShowStaffFilterDropdown] = useState(false);
  const [staffFilterSearchQuery, setStaffFilterSearchQuery] = useState('');
  const [showSemesterDropdown, setShowSemesterDropdown] = useState(false);
  const [selectedSemesters, setSelectedSemesters] = useState<string[]>(['fullyear']);
  const [showClassFilterDropdown, setShowClassFilterDropdown] = useState(false);
  const [selectedClasses, setSelectedClasses] = useState<string[]>([]);
  const [classFilterSearchQuery, setClassFilterSearchQuery] = useState('');
  const [showFlagFilterDropdown, setShowFlagFilterDropdown] = useState(false);
  const [selectedFlagFilter, setSelectedFlagFilter] = useState<string>('all'); // 'all', 'flagged', 'unflagged', or specific color

  // Class Type filter state
  const [showClassTypeDropdown, setShowClassTypeDropdown] = useState(false);
  const [selectedClassType, setSelectedClassType] = useState<'all' | 'regular' | 'composite' | 'subclass'>('all');

  // Show/hide sub-classes toggle
  const [showSubClasses, setShowSubClasses] = useState(false);

  // Group-by-subject view toggle. A subject runs once per line and each of those
  // is its own class, so ungrouped the editor shows one nearly-empty row per
  // class (RELIGION & SOCIETY12 alone is 20 rows). Grouping is purely a render
  // concern — it never rewrites grid.classes. Persisted so a coordinator's
  // choice survives a reload.
  const [groupBySubject, setGroupBySubject] = useState<boolean>(() => {
    try {
      return localStorage.getItem(GROUP_BY_SUBJECT_KEY) !== 'false';
    } catch {
      return true;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(GROUP_BY_SUBJECT_KEY, String(groupBySubject));
    } catch {
      // Storage can be unavailable (private mode); the toggle still works for
      // this session.
    }
  }, [groupBySubject]);

  // Composite class management state
  const [selectedClassIds, setSelectedClassIds] = useState<string[]>([]);
  const [showCompositeModal, setShowCompositeModal] = useState(false);
  const [showCreateClassModal, setShowCreateClassModal] = useState(false);

  // Sync internal semester state with activeSemester prop
  useEffect(() => {
    if (activeSemester === 'both') {
      setSelectedSemesters(['fullyear']);
    } else if (activeSemester === 'semester1') {
      setSelectedSemesters(['semester1']);
    } else if (activeSemester === 'semester2') {
      setSelectedSemesters(['semester2']);
    }
  }, [activeSemester]);

  // RUAH Grid Fix: Reset all filters when RUAH grid loads
  useEffect(() => {
    if (grid.name === 'RUAH') {
      debug('🔧 Resetting filters for RUAH grid');
      setSelectedCurriculumAreas([]);
      setSelectedLevels([]);
      setSelectedClasses([]);
      setSearchQuery('');
      setSelectedClassType('all');
      setShowSubClasses(false);
      setSelectedFlagFilter('all');
      // Keep semester as fullyear for RUAH (it has terms, not semesters)
      setSelectedSemesters(['fullyear']);
    }
  }, [grid.id, grid.name]);

  // Click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.curriculum-area-dropdown')) {
        setIsAreaDropdownOpen(false);
      }
      if (!target.closest('.level-dropdown')) {
        setIsLevelDropdownOpen(false);
      }
      if (!target.closest('.staff-filter-dropdown')) {
        setShowStaffFilterDropdown(false);
      }
      if (!target.closest('.semester-dropdown')) {
        setShowSemesterDropdown(false);
      }
      if (!target.closest('.class-filter-dropdown')) {
        setShowClassFilterDropdown(false);
      }
      if (!target.closest('.flag-filter-dropdown')) {
        setShowFlagFilterDropdown(false);
      }
      if (!target.closest('.class-type-dropdown')) {
        setShowClassTypeDropdown(false);
      }
    };

    if (isAreaDropdownOpen || isLevelDropdownOpen || showStaffFilterDropdown || showSemesterDropdown || showClassFilterDropdown || showFlagFilterDropdown || showClassTypeDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isAreaDropdownOpen, isLevelDropdownOpen, showStaffFilterDropdown, showSemesterDropdown, showClassFilterDropdown, showFlagFilterDropdown, showClassTypeDropdown]);

  // ✅ FIXED: Memoized semester generation
  const effectiveSemesters = useMemo(() => {
    // If semesters exist in the grid, use them
    if (grid.semesters && grid.semesters.length > 0) {
      return grid.semesters;
    }
    // Otherwise, generate default semesters based on available lines
    const allLineIds = grid.lines.map(line => line?.id || line || '').filter(Boolean);
    // Determine semester 1 and semester 2 lines based on line properties or IDs
    const semester1Lines = grid.lines.filter(line => {
      const lineId = line?.id || line;
      if (!lineId || typeof lineId !== 'string') return false;
      return line?.semester === 'semester1' ||
             line?.studyPeriod === 'semester1' ||
             ['A', 'B', 'C', 'D', 'E', 'F'].includes(lineId) ||
             lineId.includes('S1') ||
             lineId.endsWith('1');
    }).map(line => line?.id || line || '').filter(Boolean);
    
    const semester2Lines = grid.lines.filter(line => {
      const lineId = line?.id || line;
      if (!lineId || typeof lineId !== 'string') return false;
      return line?.semester === 'semester2' ||
             line?.studyPeriod === 'semester2' ||
             ['G', 'H', 'I', 'J', 'K', 'L'].includes(lineId) ||
             lineId.includes('S2') ||
             (lineId.endsWith('2') && !lineId.includes('S1'));
    }).map(line => line?.id || line || '').filter(Boolean);
    const defaultSemesters = [{
      id: 'fullyear',
      name: 'Full Year',
      lineIds: allLineIds
    }, {
      id: 'semester1',
      name: 'Semester 1',
      lineIds: semester1Lines
    }, {
      id: 'semester2',
      name: 'Semester 2',
      lineIds: semester2Lines
    }].filter(semester => semester.lineIds.length > 0);
    return defaultSemesters;
  }, [grid.semesters, grid.lines]);
  // ✅ FIXED: Memoized conflict calculation
  const conflicts = useMemo(() => {
    const conflictSet = new Set<string>();
    const teacherAssignments = new Map<string, Array<{
      classId: string;
      lineId: string;
    }>>();
    
    grid.classes.forEach(classItem => {
      Object.entries(classItem.lineAssignments || {}).forEach(([lineId, assignmentData]) => {
        if (assignmentData) {
          // Handle multi-teacher assignments (stored as JSON)
          let teachers: string[] = [];
          
          // Check if it's a JSON string (multi-teacher assignment)
          if (assignmentData.startsWith('[') || assignmentData.startsWith('{')) {
            try {
              const parsed = JSON.parse(assignmentData);
              if (Array.isArray(parsed)) {
                teachers = parsed.map(t => t.teacherCode);
              } else if (parsed.teacherCode) {
                teachers = [parsed.teacherCode];
              }
            } catch (e) {
              // If parsing fails, treat as single teacher
              teachers = [assignmentData];
            }
          } else {
            // Single teacher assignment
            teachers = [assignmentData];
          }
          
          // Add each teacher to the tracking map
          teachers.forEach(teacherCode => {
            if (!teacherAssignments.has(teacherCode)) {
              teacherAssignments.set(teacherCode, []);
            }
            teacherAssignments.get(teacherCode)!.push({
              classId: classItem.id,
              lineId
            });
          });
        }
      });
    });
    // Check for conflicts (same teacher, same line, different classes)
    teacherAssignments.forEach((assignments, teacherCode) => {
      const lineGroups = new Map<string, Array<{
        classId: string;
      }>>();
      assignments.forEach(({
        classId,
        lineId
      }) => {
        if (!lineGroups.has(lineId)) {
          lineGroups.set(lineId, []);
        }
        lineGroups.get(lineId)!.push({
          classId
        });
      });
      // If a teacher is assigned to multiple classes in the same line = conflict
      lineGroups.forEach((classes, lineId) => {
        if (classes.length > 1) {
          classes.forEach(({
            classId
          }) => {
            conflictSet.add(`${classId}-${lineId}`);
          });
        }
      });
    });
    return conflictSet;
  }, [grid.classes]);
  // ✅ FIXED: Memoized cell status calculation
  const getCellStatus = useCallback((classId: string, lineId: string, teacherCodeRaw: string | null) => {
    if (!teacherCodeRaw) return 'empty';
    const cellKey = `${classId}-${lineId}`;
    
    // First check if this cell itself is marked as a conflict
    if (conflicts.has(cellKey)) return 'conflict';
    
    // For multi-teacher assignments, check if any of the teachers have conflicts
    if (teacherCodeRaw && (teacherCodeRaw.startsWith('[') || teacherCodeRaw.startsWith('{'))) {
      try {
        const parsedData = JSON.parse(teacherCodeRaw);
        let teachers: Array<{teacherCode: string, minutes: number}> = [];
        
        if (Array.isArray(parsedData)) {
          teachers = parsedData;
        } else if (parsedData.teacherCode) {
          teachers = [parsedData];
        }
        
        // Check if any of these teachers are assigned to another class in the same line
        for (const teacher of teachers) {
          // Look through all classes to see if this teacher is assigned elsewhere in this line
          for (const otherClass of grid.classes) {
            if (otherClass.id === classId) continue; // Skip the current class
            
            const otherAssignment = otherClass.lineAssignments?.[lineId];
            if (!otherAssignment) continue;
            
            // Check if the other assignment includes this teacher
            if (otherAssignment === teacher.teacherCode) {
              // Direct single teacher conflict
              return 'conflict';
            } else if (otherAssignment.startsWith('[') || otherAssignment.startsWith('{')) {
              // Check multi-teacher assignment
              try {
                const otherParsed = JSON.parse(otherAssignment);
                const otherTeachers = Array.isArray(otherParsed) ? otherParsed : [otherParsed];
                if (otherTeachers.some(t => t.teacherCode === teacher.teacherCode)) {
                  return 'conflict';
                }
              } catch (e) {
                // Ignore parse errors
              }
            }
          }
        }
        
        // Check for incomplete assignment (doesn't add up to DEFAULT_MINUTES_PER_LINE minutes)
        const totalMinutes = teachers.reduce((sum, assignment) => sum + (assignment.minutes || 0), 0);
        if (totalMinutes < DEFAULT_MINUTES_PER_LINE) {
          return 'incomplete';
        }
      } catch (e) {
        // If parsing fails, ignore
      }
    }
    
    return 'assigned';
  }, [conflicts, grid.classes]);
  // ✅ FIXED: Memoized filtered classes
  const filteredClasses = useMemo(() => {
    // DEBUG: Log for RUAH grid
    if (grid.name === 'RUAH') {
      debug('🔍 StaffAllocationGrid - RUAH:');
      debug('  - grid.classes.length:', grid.classes?.length || 0);
      debug('  - selectedClassType:', selectedClassType);
      debug('  - selectedCurriculumAreas:', selectedCurriculumAreas);
      debug('  - selectedLevels:', selectedLevels);
      debug('  - selectedClasses:', selectedClasses);
      debug('  - staffFilter:', staffFilter);
    }

    let classes = grid.classes;

    // Filter by class type
    switch (selectedClassType) {
      case 'all':
        // Show regular classes and composites
        // Hide sub-classes by default unless showSubClasses toggle is on
        if (!showSubClasses) {
          const beforeFilter = classes.length;
          classes = classes.filter(c => !c.isCompositeSubClass);
          if (grid.name === 'RUAH' && beforeFilter !== classes.length) {
            debug(`  - Filtered by subclass: ${beforeFilter} → ${classes.length}`);
          }
        }
        break;
      case 'regular':
        // Only show classes that are NOT composite and NOT sub-class
        classes = classes.filter(c => !c.isComposite && !c.isCompositeSubClass);
        break;
      case 'composite':
        // Only show composite classes
        classes = classes.filter(c => c.isComposite === true);
        break;
      case 'subclass':
        // Only show sub-classes
        classes = classes.filter(c => c.isCompositeSubClass === true);
        break;
    }

    // Filter by curriculum areas (multi-select)
    if (selectedCurriculumAreas.length > 0) {
      const beforeFilter = classes.length;
      classes = classes.filter(c => selectedCurriculumAreas.includes(c.curriculumArea || ''));
      if (grid.name === 'RUAH' && beforeFilter !== classes.length) {
        debug(`  - Filtered by curriculum: ${beforeFilter} → ${classes.length}`);
        debug('    Selected areas:', selectedCurriculumAreas);
        debug('    Class areas:', [...new Set(grid.classes.map(c => c.curriculumArea))]);
      }
    }

    // Filter by levels (multi-select)
    if (selectedLevels.length > 0) {
      const beforeFilter = classes.length;
      classes = classes.filter(c => {
        const yearLevel = c.yearLevel || c.level || extractClassLevel(c.className);
        if (yearLevel) {
          const levelKey = typeof yearLevel === 'number' ? `Year ${yearLevel}` : yearLevel;
          return selectedLevels.includes(levelKey);
        }
        return false;
      });
      if (grid.name === 'RUAH' && beforeFilter !== classes.length) {
        debug(`  - Filtered by levels: ${beforeFilter} → ${classes.length}`);
        debug('    Selected levels:', selectedLevels);
      }
    }

    // Filter by selected classes (multi-select)
    if (selectedClasses.length > 0) {
      const beforeFilter = classes.length;
      classes = classes.filter(c => selectedClasses.includes(c.className || ''));
      if (grid.name === 'RUAH' && beforeFilter !== classes.length) {
        debug(`  - Filtered by class names: ${beforeFilter} → ${classes.length}`);
        debug('    Selected classes count:', selectedClasses.length);
      }
    }
    
    // Free-text search across the fields a user would recognise a subject by:
    // the class/subject name, its subject code, and its learning area. Matching
    // only className meant typing "media" missed classes coded MED10 or filed
    // under a differently-named area.
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      classes = classes.filter(c =>
        [c.className, c.subjectCode, c.curriculumArea].some(
          field => (field || '').toLowerCase().includes(query)
        )
      );
    }
    
    // Filter by selected staff (show only classes with assignments to selected staff)
    if (staffFilter.length > 0) {
      classes = classes.filter(classItem => {
        // Check if any line assignment in this class is assigned to one of the selected staff
        return Object.values(classItem.lineAssignments || {}).some(assignment => {
          if (!assignment) return false;

          // Handle both single teacher assignments (string) and multi-teacher assignments (JSON string)
          let assignedTeachers: string[] = [];

          if (typeof assignment === 'string') {
            try {
              // Try to parse as JSON (multi-teacher assignment)
              const parsed = JSON.parse(assignment);
              if (Array.isArray(parsed)) {
                assignedTeachers = parsed.map(teacher => teacher.teacherCode || teacher).filter(Boolean);
              } else if (parsed.teachers && Array.isArray(parsed.teachers)) {
                assignedTeachers = parsed.teachers.map(teacher => teacher.teacherCode || teacher).filter(Boolean);
              } else if (parsed.teacherCode) {
                assignedTeachers = [parsed.teacherCode];
              } else {
                // Single teacher assignment stored as JSON string
                assignedTeachers = [assignment.replace(/"/g, '')];
              }
            } catch {
              // Single teacher assignment as plain string
              assignedTeachers = [assignment];
            }
          } else if (typeof assignment === 'object' && assignment.teachers) {
            // Multi-teacher assignment object
            assignedTeachers = assignment.teachers.map(teacher => teacher.teacherCode || teacher).filter(Boolean);
          } else if (typeof assignment === 'object' && assignment.teacherCode) {
            assignedTeachers = [assignment.teacherCode];
          }

          // Use the enhanced matching logic to check if any assigned teacher matches the filter
          return assignedTeachers.some(teacherCode => isTeacherInStaffFilter(teacherCode, staffFilter));
        });
      });
    }

    // Filter by flag status
    if (selectedFlagFilter !== 'all') {
      classes = classes.filter(classItem => {
        const classFlags = flags.filter(flag => flag.classId === classItem.id);

        if (selectedFlagFilter === 'flagged') {
          return classFlags.length > 0;
        } else if (selectedFlagFilter === 'unflagged') {
          return classFlags.length === 0;
        } else {
          // Filter by specific flag color
          return classFlags.some(flag => flag.color === selectedFlagFilter);
        }
      });
    }

    // DEBUG: Log final filtered result for RUAH
    if (grid.name === 'RUAH') {
      debug('  - FINAL filteredClasses.length:', classes.length);
      if (classes.length === 0) {
        console.warn('  ⚠️ WARNING: All classes were filtered out!');
      } else {
        debug('  - Sample class:', classes[0]?.className);
      }
    }

    return classes.sort((a, b) => (a.className || '').localeCompare(b.className || ''));
  }, [grid.classes, selectedCurriculumAreas, selectedLevels, selectedClasses, searchQuery, staffFilter, selectedFlagFilter, flags, selectedClassType, showSubClasses]);
  // Helper function to sort lines by semester and alphabetical order
  const sortLines = (lines: any[]) => {
    return [...lines].sort((a, b) => {
      const lineA = a?.id || a;
      const lineB = b?.id || b;

      // Get study period for each line
      const studyPeriodA = a?.studyPeriod || 'semester1'; // Default to semester1 if not specified
      const studyPeriodB = b?.studyPeriod || 'semester1';

      // Determine semester order based on study period
      const getSemesterOrder = (studyPeriod: string) => {
        if (studyPeriod === 'semester1') return 1;
        if (studyPeriod === 'semester2') return 2;
        return 3; // For any other study periods
      };

      const semesterOrderA = getSemesterOrder(studyPeriodA);
      const semesterOrderB = getSemesterOrder(studyPeriodB);

      // First sort by semester
      if (semesterOrderA !== semesterOrderB) {
        return semesterOrderA - semesterOrderB;
      }

      // Within same semester, sort alphabetically by line ID
      return String(lineA).localeCompare(String(lineB));
    });
  };

  // ✅ FIXED: Memoized filtered lines with proper sorting
  const filteredLines = useMemo(() => {
    // DEBUG: Log for RUAH grid
    if (grid.name === 'RUAH') {
      debug('🔍 filteredLines - RUAH:');
      debug('  - grid.lines:', grid.lines.map(l => l.id || l));
      debug('  - selectedSemesters:', selectedSemesters);
      debug('  - effectiveSemesters:', effectiveSemesters.map(s => s.id));
    }

    let linesToFilter = grid.lines;

    if (!selectedSemesters.includes('fullyear') && selectedSemesters.length > 0) {
      // Get all line IDs for selected semesters
      const allowedLineIds = new Set<string>();

      // First, try to get line IDs from semester definitions
      selectedSemesters.forEach(semesterId => {
        const semester = effectiveSemesters.find(s => s.id === semesterId);
        if (semester) {
          // Handle different property names: lineIds (generated) vs lines (from database)
          const lineList = semester.lineIds || semester.lines || [];
          lineList.forEach(lineId => allowedLineIds.add(lineId));
        }
      });

      // Fallback: directly filter lines based on their studyPeriod
      linesToFilter = grid.lines.filter(line => {
        const lineId = line?.id || line;

        // Check if line is explicitly in allowed list
        if (lineId && allowedLineIds.has(lineId)) {
          return true;
        }

        // Fallback: check line's studyPeriod directly
        if (line?.studyPeriod && selectedSemesters.includes(line.studyPeriod)) {
          return true;
        }

        // Final fallback: use our sorting logic to determine semester
        if (typeof lineId === 'string') {
          const studyPeriod = line?.studyPeriod || 'semester1'; // Default to semester1 if not specified
          const getSemesterOrder = (studyPeriod: string) => {
            if (studyPeriod === 'semester1') return 1;
            if (studyPeriod === 'semester2') return 2;
            return 3; // For any other study periods
          };

          // If line doesn't have explicit studyPeriod, determine it like in our sorting function
          if (!line?.studyPeriod) {
            // Traditional line mappings
            if (['A', 'B', 'C', 'D', 'E', 'F'].includes(lineId.toUpperCase())) {
              return selectedSemesters.includes('semester1');
            }
            if (['G', 'H', 'I', 'J', 'K', 'L'].includes(lineId.toUpperCase())) {
              return selectedSemesters.includes('semester2');
            }
          }

          return selectedSemesters.includes(studyPeriod);
        }

        return false;
      });
    }

    // Always apply sorting for consistent ordering
    const sorted = sortLines(linesToFilter);

    // DEBUG: Log for RUAH grid
    if (grid.name === 'RUAH') {
      debug('  - FINAL filteredLines:', sorted.map(l => l.id || l));
      if (sorted.length === 0) {
        console.warn('  ⚠️ WARNING: All lines were filtered out!');
      }
    }

    return sorted;
  }, [grid.lines, effectiveSemesters, selectedSemesters]);

  /**
   * The rows the table actually renders.
   *
   * Ungrouped, this is one row per class — identical to the pre-grouping editor.
   * Grouped, plain classes sharing a `className` collapse into one row while
   * composites and their sub-classes keep their own rows, because those carry
   * bespoke rendering and their minutes are deliberately excluded from load
   * totals. Grouping them would misreport teacher load, which is the one thing
   * this view must never do.
   *
   * Order is preserved from filteredClasses: a group appears where its first
   * member appeared, so existing sorting and filtering still drive the layout.
   */
  const displayRows = useMemo<DisplayRow[]>(() => {
    if (!groupBySubject) {
      return filteredClasses.map(c => ({
        key: c.id,
        label: formatClassDisplayName(c),
        curriculumArea: c.curriculumArea,
        classes: [c],
        isGroup: false,
      }));
    }

    const rows: DisplayRow[] = [];
    const groupIndexByKey = new Map<string, number>();

    for (const classItem of filteredClasses) {
      const groupable = !classItem.isComposite && !classItem.isCompositeSubClass;
      const name = (classItem.className || '').trim();

      if (!groupable || !name) {
        rows.push({
          key: classItem.id,
          label: formatClassDisplayName(classItem),
          curriculumArea: classItem.curriculumArea,
          classes: [classItem],
          isGroup: false,
        });
        continue;
      }

      // Key on name + curriculum area so two unrelated subjects that happen to
      // share a name never merge.
      const groupKey = `${name}::${classItem.curriculumArea || ''}`;
      const existing = groupIndexByKey.get(groupKey);
      if (existing === undefined) {
        groupIndexByKey.set(groupKey, rows.length);
        rows.push({
          key: `group:${groupKey}`,
          label: name,
          curriculumArea: classItem.curriculumArea,
          classes: [classItem],
          isGroup: true,
        });
      } else {
        rows[existing].classes.push(classItem);
      }
    }

    return rows;
  }, [filteredClasses, groupBySubject]);

  /**
   * For each row, the classes that occupy each visible line, plus how many slots
   * the tallest cell needs. That count is what drives the row's height, so a
   * subject running three parallel classes on one line gets a three-slot-tall
   * row instead of three squashed slivers.
   */
  const rowLayouts = useMemo(() => {
    return displayRows.map(row => {
      const lineIds: string[] = filteredLines.map(line => line?.id || line);
      const byLine = new Map<string, ClassAllocation[]>();

      // Ungrouped rows keep the old behaviour exactly: the single class gets a
      // cell on every line whether or not it runs there — the cell's own styling
      // is what communicates that.
      if (!row.isGroup) {
        for (const lineId of lineIds) byLine.set(lineId, row.classes);
        return { byLine, maxSlots: 1 };
      }

      const placed = new Set<string>();
      for (const lineId of lineIds) {
        const occupants = row.classes.filter(c => classOccupiesLine(c, lineId, subjectLineAvailability));
        occupants.forEach(c => placed.add(c.id));
        byLine.set(lineId, occupants);
      }

      // A class with no assignment, no allowed lines and no availability entry
      // would land on no line at all and become impossible to assign — the one
      // thing collapsing a row must never do. Give it a slot on every line, the
      // same reach it has in the ungrouped view.
      const unplaced = row.classes.filter(c => !placed.has(c.id));
      if (unplaced.length > 0) {
        for (const lineId of lineIds) {
          byLine.set(lineId, [...(byLine.get(lineId) || []), ...unplaced]);
        }
      }

      // The safety net above only rescues a class placed on NO line. A class
      // placed on one line was still unreachable on every other: once both
      // DISCOVER - HPE classes were assigned to line A, columns B and C held no
      // slot at all, so there was nothing to click and the class could never be
      // added to a second line (issue #227). 15 classes in the 2027 grid were
      // locked this way.
      //
      // Give any COLUMN THAT WOULD OTHERWISE BE EMPTY a single slot. This cannot
      // reinflate the row — maxSlots is the max across columns, and a column that
      // had zero occupants can only take it from 0 to 1 — so the 27-slot-tall
      // DISCOVER - MATHS row that classOccupiesLine exists to prevent stays
      // prevented. It also brings grouped rows in line with the ungrouped view,
      // where every cell is already clickable regardless of placement.
      //
      // Prefer a class with no assignment anywhere, so the click lands on a spare
      // rather than adding a second line to a class already placed elsewhere.
      if (row.classes.length > 0) {
        const spare = row.classes.find(
          c => Object.keys(c.lineAssignments || {}).length === 0
        );
        for (const lineId of lineIds) {
          if ((byLine.get(lineId) || []).length === 0) {
            byLine.set(lineId, [spare || row.classes[0]]);
          }
        }
      }

      let maxSlots = 1;
      for (const occupants of byLine.values()) {
        if (occupants.length > maxSlots) maxSlots = occupants.length;
      }

      return { byLine, maxSlots };
    });
  }, [displayRows, filteredLines, subjectLineAvailability]);
  // ✅ FIXED: Memoized curriculum area counts and list
  const curriculumAreaCounts = useMemo(() => {
    const counts = new Map<string, number>();
    // Count all classes by curriculum area
    grid.classes.forEach(classItem => {
      const area = classItem.curriculumArea || 'Unknown';
      counts.set(area, (counts.get(area) || 0) + 1);
    });

    // Debug logging to understand what's happening
    debug('🔍 Curriculum Area Counts:', Object.fromEntries(counts));
    debug('🔍 Sample classes:', grid.classes.slice(0, 3).map(c => ({ name: c.className, curriculum: c.curriculumArea })));

    return counts;
  }, [grid.classes]);

  // Generate curriculum areas list - always provide full list plus any custom areas from classes
  const availableCurriculumAreas = useMemo(() => {
    const areas = new Set<string>();

    // Always start with the default curriculum areas as a baseline
    const defaultAreas = [
      'AGRICULTURE',
      'ARTS',
      'ENGLISH',
      'HPE',
      'HUMANITIES',
      'LANGUAGES',
      'LEARNING SUPPORT',
      'MATHEMATICS',
      'RE',
      'SCIENCE',
      'STEAM',
      'TECHNOLOGY',
      'VM/VET'
    ];
    defaultAreas.forEach(area => areas.add(area));

    // Add any curriculum areas from the grid's configuration
    if (grid.curriculumAreas && grid.curriculumAreas.length > 0) {
      grid.curriculumAreas.forEach(area => areas.add(area.name));
    }

    // Add any curriculum areas from Main Grid (in case they have custom ones).
    // Match on THIS grid's academic year — allGrids spans every year, so an
    // unqualified name match can pull another year's areas in (issue #165).
    const mainGrid = allGrids.find(
      g => g.name === 'Main Grid' && (g.year == null || grid.year == null || g.year === grid.year)
    );
    if (mainGrid && mainGrid.curriculumAreas && mainGrid.curriculumAreas.length > 0) {
      mainGrid.curriculumAreas.forEach(area => areas.add(area.name));
    }

    // Additionally include any curriculum areas from existing classes (for custom areas)
    grid.classes.forEach(classItem => {
      if (classItem.curriculumArea) {
        areas.add(classItem.curriculumArea);
      }
    });

    return Array.from(areas).filter(area => area).sort().map(area => ({
      id: (area || '').toLowerCase().replace(/\s+/g, '_'),
      name: area
    }));
  }, [grid.classes, grid.curriculumAreas, grid.year, allGrids]);
  
  // Memoized level counts - using yearLevel from imported data
  const levelCounts = useMemo(() => {
    const counts = new Map<string, number>();
    
    // Count classes by yearLevel
    grid.classes.forEach(classItem => {
      const yearLevel = classItem.yearLevel || classItem.level || extractClassLevel(classItem.className);
      if (yearLevel) {
        const levelKey = typeof yearLevel === 'number' ? `Year ${yearLevel}` : yearLevel;
        counts.set(levelKey, (counts.get(levelKey) || 0) + 1);
      }
    });
    
    return counts;
  }, [grid.classes]);
  // ✅ FIXED: Memoized curriculum color getter - use dynamic areas
  const getCurriculumColor = useCallback((curriculumArea?: string | null) => {
    // Generate a consistent color hash for each curriculum area
    const colors = [
      '#EBF8FF', '#FFF5F5', '#F0FFF4', '#FFFBEB', '#FAF5FF',
      '#F0F9FF', '#FEF7F0', '#F7FAFC', '#FFFAF0', '#F9FAFB'
    ];

    // A class may have no curriculum area yet - fall back to a neutral colour
    // rather than crashing on `.length` of undefined.
    if (!curriculumArea) {
      return colors[colors.length - 1];
    }

    // Try to find in static grid areas first (for backward compatibility)
    const staticArea = grid.curriculumAreas?.find(ca => ca.id === curriculumArea || ca.name === curriculumArea);
    if (staticArea?.color) {
      return staticArea.color;
    }

    let hash = 0;
    for (let i = 0; i < curriculumArea.length; i++) {
      hash = curriculumArea.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
  }, [grid.curriculumAreas]);
  // ✅ FIXED: Memoized line display info
  const getLineDisplayInfo = useCallback((line: any) => {
    // Safe access to line.id with null/undefined checks
    const lineId = line?.id || line || '';
    const lineSemester = line?.semester || line?.studyPeriod;

    // If line.id contains 'S1' or ends with numbers like '1', it's semester 1
    if ((typeof lineId === 'string' && lineId.includes('S1')) ||
        lineSemester === 'semester1' ||
        ['A', 'B', 'C', 'D', 'E', 'F'].includes(lineId)) {
      return {
        semester: 'Semester 1',
        semesterClass: 'text-navy-600'
      };
    }
    // If line.id contains 'S2' or line.semester is semester2, it's semester 2
    if ((typeof lineId === 'string' && lineId.includes('S2')) ||
        lineSemester === 'semester2' ||
        ['G', 'H', 'I', 'J', 'K', 'L'].includes(lineId)) {
      return {
        semester: 'Semester 2',
        semesterClass: 'text-ok-fg'
      };
    }
    // Fallback - try to determine from line.semester property
    if (line.semester === 'semester1') {
      return {
        semester: 'Semester 1',
        semesterClass: 'text-navy-600'
      };
    } else if (line.semester === 'semester2') {
      return {
        semester: 'Semester 2',
        semesterClass: 'text-ok-fg'
      };
    }
    // Default fallback
    return {
      semester: 'Unknown',
      semesterClass: 'text-ink-500'
    };
  }, []);
  // ✅ FIXED: Memoized cell click handler
  const handleCellClick = useCallback((classId: string, lineId: string) => {
    debug('🔍 Cell clicked:', { classId, lineId });
    const classItem = grid.classes.find(c => c.id === classId);
    debug('🔍 Found class:', classItem);
    debug('🔍 Class lineAssignments:', classItem?.lineAssignments);

    // If this is a sub-class, redirect to the parent composite for assignment
    let targetClassId = classId;
    if (classItem?.isCompositeSubClass && classItem.parentCompositeId) {
      debug('🔄 Sub-class clicked, redirecting to parent composite:', classItem.parentCompositeId);
      targetClassId = classItem.parentCompositeId;

      // Verify parent composite exists
      const parentComposite = grid.classes.find(c => c.id === targetClassId);
      if (!parentComposite) {
        console.error('❌ Parent composite not found for sub-class:', classId);
        addToast('Error: Parent composite class not found. Please refresh and try again.', 'error');
        return;
      }
    }

    setSelectedCell({
      classId: targetClassId,
      lineId
    });
  }, [grid.classes]);
  // Handle line name editing
  const handleLineNameEdit = useCallback((lineId: string, currentName: string) => {
    setEditingLineId(lineId);
    setEditingLineName(currentName);
  }, []);
  const handleLineNameSave = useCallback(() => {
    if (editingLineId && onLineUpdate) {
      onLineUpdate(editingLineId, editingLineName);
    }
    setEditingLineId(null);
    setEditingLineName('');
  }, [editingLineId, editingLineName, onLineUpdate]);
  const handleLineNameCancel = useCallback(() => {
    setEditingLineId(null);
    setEditingLineName('');
  }, []);
  // Handle line management changes
  const handleLineManagementSave = useCallback(async (newLines: any[], newSemesters: any[]) => {
    if (!onGridUpdate) {
      return;
    }
    try {
      await onGridUpdate({
        lines: newLines,
        semesters: newSemesters
      });
    } catch (error) {
      throw error;
    }
  }, [onGridUpdate]);
  // ✅ FIXED: Optimized teacher assignment handler
  const handleTeacherAssignmentWithDrafting = useCallback((teacherCode: string | null) => {
    debug('🎯 handleTeacherAssignmentWithDrafting called:', { teacherCode, selectedCell });

    if (!selectedCell) {
      debug('❌ No selected cell');
      return;
    }
    const {
      classId,
      lineId
    } = selectedCell;

    debug('🎯 Attempting assignment:', { classId, lineId, teacherCode });

    try {
      // Call the parent's assignment handler
      debug('🎯 Calling onAssignTeacher...');
      onAssignTeacher(classId, lineId, teacherCode);
      debug('✅ onAssignTeacher completed successfully');

      // Handle paired semester assignment
      const line = grid.lines.find(l => l.id === lineId);
      const classItem = grid.classes.find(c => c.id === classId);
      if (line?.pairedLineId && teacherCode && classItem) {
        // Check if the paired line is empty
        if (!classItem.lineAssignments[line.pairedLineId]) {
          debug('🎯 Assigning to paired line:', line.pairedLineId);
          onAssignTeacher(classId, line.pairedLineId, teacherCode);
        }
      }
      // Clear the selected cell
      setSelectedCell(null);
      debug('✅ Assignment completed successfully');
    } catch (error) {
      console.error('❌ ERROR in handleTeacherAssignmentWithDrafting:', error);
      console.error('❌ Error details:', {
        message: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : undefined,
        error
      });
      throw error; // Re-throw instead of silently swallowing
    }
  }, [selectedCell, onAssignTeacher, grid.lines, grid.classes]);
  // New handler for multi-teacher assignments
  const handleMultiTeacherAssignment = useCallback((assignments: {
    teacherCode: string;
    minutes: number;
  }[]) => {
    if (!selectedCell) {
      return;
    }
    const {
      classId,
      lineId
    } = selectedCell;
    try {
      // If no assignments or empty array, clear the assignment
      if (!assignments || assignments.length === 0) {
        onAssignTeacher(classId, lineId, null);
      }
      // If only one teacher with full minutes, use the simple assignment
      else if (assignments.length === 1 && assignments[0].minutes === DEFAULT_MINUTES_PER_LINE) {
        onAssignTeacher(classId, lineId, assignments[0].teacherCode);
      }
      // Otherwise, serialize the assignments as JSON
      else {
        // Convert to JSON string to store in the existing data structure
        const assignmentData = JSON.stringify(assignments);
        onAssignTeacher(classId, lineId, assignmentData);
      }
      // Clear the selected cell
      setSelectedCell(null);
    } catch (error) {
      // Silently handle errors
    }
  }, [selectedCell, onAssignTeacher]);

  // Composite class handlers
  const handleToggleClassSelection = useCallback((classId: string) => {
    setSelectedClassIds(prev => {
      if (prev.includes(classId)) {
        return prev.filter(id => id !== classId);
      } else {
        return [...prev, classId];
      }
    });
  }, []);

  /**
   * Toggle every class on a row. Ungrouped that is a single class, so this
   * behaves exactly like the old per-class checkbox; grouped it selects or
   * clears the whole subject at once, keeping bulk assign usable from the
   * collapsed view.
   */
  const handleToggleRowSelection = useCallback((classIds: string[]) => {
    setSelectedClassIds(prev => {
      const allSelected = classIds.every(id => prev.includes(id));
      if (allSelected) {
        return prev.filter(id => !classIds.includes(id));
      }
      const next = new Set(prev);
      classIds.forEach(id => next.add(id));
      return Array.from(next);
    });
  }, []);

  const handleSelectAllClasses = useCallback(() => {
    const selectableClasses = filteredClasses
      .filter(c => !c.isCompositeSubClass) // Can't select sub-classes
      .map(c => c.id);

    if (selectedClassIds.length === selectableClasses.length) {
      // Deselect all
      setSelectedClassIds([]);
    } else {
      // Select all
      setSelectedClassIds(selectableClasses);
    }
  }, [filteredClasses, selectedClassIds.length]);

  const handleCreateComposite = useCallback(async (compositeName: string, subClassIds: string[]) => {
    try {
      // Call gridsService to create the composite class (returns updated grid without persisting)
      const updatedGrid = await gridsService.createCompositeClass(grid.id, compositeName, subClassIds);

      // Notify parent to update grid data - this will persist to database once
      if (onGridUpdate) {
        await onGridUpdate({
          classes: updatedGrid.classes,
          // Pass other necessary grid properties to ensure full update
          lines: updatedGrid.lines,
          semesters: updatedGrid.semesters
        });
      }

      // Close modal and reset selection after successful update
      setShowCompositeModal(false);
      setSelectedClassIds([]);
    } catch (error) {
      console.error('Error creating composite class:', error);
      addToast(`Failed to create composite class: ${error instanceof Error ? error.message : 'Unknown error'}`, 'error');
    }
  }, [grid.id, onGridUpdate]);

  const handleBreakApartComposite = useCallback(async (compositeClassId: string) => {
    try {
      // Confirm action with user
      if (!(await confirm({
        title: 'Break apart composite class?',
        message: 'Are you sure you want to break apart this composite class? This will restore the original individual classes.',
        confirmLabel: 'Break apart',
        variant: 'danger',
      }))) {
        return;
      }

      debug('🔧 Breaking apart composite class:', compositeClassId);

      // Check if onGridUpdate is available
      if (!onGridUpdate) {
        console.error('❌ onGridUpdate is not provided - cannot break apart composite class');
        addToast('Unable to break apart composite class: Update function not available. Please refresh the page and try again.', 'error');
        return;
      }

      // Call gridsService to break apart the composite class (returns updated grid without persisting)
      const updatedGrid = await gridsService.breakApartCompositeClass(grid.id, compositeClassId);
      debug('✅ Composite class broken apart successfully, updating grid with', updatedGrid.classes.length, 'classes');

      // Notify parent to update grid data - this will persist to database once
      await onGridUpdate({
        classes: updatedGrid.classes,
        // Pass other necessary grid properties to ensure full update
        lines: updatedGrid.lines,
        semesters: updatedGrid.semesters
      });

      debug('✅ Grid updated successfully after breaking apart composite');
    } catch (error) {
      console.error('❌ Error breaking apart composite class:', error);
      addToast(`Failed to break apart composite class: ${error instanceof Error ? error.message : 'Unknown error'}`, 'error');
    }
  }, [grid.id, onGridUpdate]);

  // Delete selected classes handler
  const handleDeleteSelectedClasses = useCallback(async () => {
    if (selectedClassIds.length === 0) {
      return;
    }

    try {
      // Get the selected classes
      const selectedClasses = grid.classes.filter(c => selectedClassIds.includes(c.id));

      // Check for sub-classes (these cannot be deleted directly)
      const subClasses = selectedClasses.filter(c => c.isCompositeSubClass);
      if (subClasses.length > 0) {
        addToast(
          `Cannot delete sub-classes directly.\n\n` +
          `The following classes are part of composite classes:\n${subClasses.map(c => `• ${c.className}`).join('\n')}\n\n` +
          `To delete these, you must either:\n` +
          `1. Break apart the parent composite class first, or\n` +
          `2. Delete the entire composite class`,
          'warning'
        );
        return;
      }

      // Check for composite classes
      const compositeClasses = selectedClasses.filter(c => c.isComposite);
      const regularClasses = selectedClasses.filter(c => !c.isComposite && !c.isCompositeSubClass);

      // Build confirmation message
      let confirmMessage = `Are you sure you want to delete ${selectedClasses.length} class${selectedClasses.length > 1 ? 'es' : ''}?\n\n`;

      if (regularClasses.length > 0) {
        confirmMessage += `Regular classes (${regularClasses.length}):\n${regularClasses.map(c => `• ${c.className}`).join('\n')}\n\n`;
      }

      if (compositeClasses.length > 0) {
        confirmMessage += `Composite classes (${compositeClasses.length}):\n${compositeClasses.map(c => `• ${c.className}`).join('\n')}\n\n`;
        confirmMessage += `Note: Deleting composite classes will also restore their sub-classes as regular classes.\n\n`;
      }

      confirmMessage += `This action cannot be undone.`;

      if (!(await confirm({
        title: `Delete ${selectedClasses.length} class${selectedClasses.length > 1 ? 'es' : ''}?`,
        message: confirmMessage,
        confirmLabel: 'Delete',
        variant: 'danger',
      }))) {
        return;
      }

      // Handle deletion
      let updatedClasses = [...grid.classes];

      for (const compositeClass of compositeClasses) {
        // First, restore the sub-classes by removing their composite markers
        updatedClasses = updatedClasses.map(c => {
          if (c.parentCompositeId === compositeClass.id) {
            // Restore the sub-class
            const { isCompositeSubClass, parentCompositeId, ...restoredClass } = c;
            return restoredClass;
          }
          return c;
        });

        // Then remove the composite class itself
        updatedClasses = updatedClasses.filter(c => c.id !== compositeClass.id);
      }

      // Remove regular classes
      updatedClasses = updatedClasses.filter(c => !regularClasses.some(rc => rc.id === c.id));

      // Update the grid
      if (onGridUpdate) {
        await onGridUpdate({
          classes: updatedClasses,
          lines: grid.lines,
          semesters: grid.semesters
        });
      }

      // Clear selection
      setSelectedClassIds([]);

      // Show success message
      const deletedCount = selectedClasses.length;
      addToast(`Successfully deleted ${deletedCount} class${deletedCount > 1 ? 'es' : ''}`, 'success');

    } catch (error) {
      console.error('Error deleting classes:', error);
      addToast(`Failed to delete classes: ${error instanceof Error ? error.message : 'Unknown error'}`, 'error');
    }
  }, [selectedClassIds, grid.classes, grid.id, grid.lines, grid.semesters, onGridUpdate]);

  // Create new class handler
  const handleCreateClass = useCallback(async (newClassData: Omit<ClassAllocation, 'id'>) => {
    try {
      // Generate unique ID for the new class
      const classId = `${newClassData.className.replace(/\s+/g, '_')}_${Date.now()}`;

      // Create the new class with the generated ID
      const newClass: ClassAllocation = {
        id: classId,
        ...newClassData
      };

      // Add the new class to the grid
      const updatedClasses = [...grid.classes, newClass];

      // Update the grid
      if (onGridUpdate) {
        await onGridUpdate({
          classes: updatedClasses,
          lines: grid.lines,
          semesters: grid.semesters
        });
      }

      // Show success message
      addToast(`Successfully created class: ${newClass.className}`, 'success');

    } catch (error) {
      console.error('Error creating class:', error);
      throw error; // Re-throw so the modal can handle it
    }
  }, [grid.classes, grid.lines, grid.semesters, onGridUpdate]);

  /**
   * Render one assignment slot — a single (class, line) cell.
   *
   * Extracted from the row map so a grouped cell can stack several of these,
   * one per parallel class a subject runs on the same line. The computation and
   * markup are unchanged from the one-row-per-class editor; only the wrapper
   * moved from <td> to <div>, with the <td> that used to own the width and
   * border now wrapping the stack.
   */
  const renderAssignmentSlot = (classItem: ClassAllocation, line: any, slotStyle: React.CSSProperties) => {
    // Sub-classes display their parent composite’s assignments.
    let displayAssignments = classItem.lineAssignments;
    if (classItem.isCompositeSubClass && classItem.parentCompositeId) {
      const parentComposite = grid.classes.find(c => c.id === classItem.parentCompositeId);
      if (parentComposite) {
        displayAssignments = parentComposite.lineAssignments;
      }
    }

                // Use displayAssignments which contains the parent composite's assignments for sub-classes
                const teacherCodeRaw = displayAssignments?.[line?.id || line];
                
                
                let teacherCode = teacherCodeRaw;
                let isMultiTeacher = false;
                let teacherAssignments = [];
                // Check if this is a multi-teacher assignment (JSON string)
                if (teacherCodeRaw && (teacherCodeRaw.startsWith('[') || teacherCodeRaw.startsWith('{') || teacherCodeRaw.includes('"teacherCode"') || teacherCodeRaw.includes('minutes'))) {
                  try {
                    // Try to parse as JSON
                    let parsedData = JSON.parse(teacherCodeRaw);
                    // Handle both array format and object format
                    if (Array.isArray(parsedData)) {
                      teacherAssignments = parsedData;
                    } else if (typeof parsedData === 'object' && parsedData !== null) {
                      // If it's a single object with teacherCode, convert to array format
                      if (parsedData.teacherCode) {
                        teacherAssignments = [parsedData];
                      }
                    }
                    isMultiTeacher = teacherAssignments.length > 0;
                    // Use the first teacher's code for status calculation
                    teacherCode = isMultiTeacher ? teacherAssignments[0].teacherCode : null;
                  } catch (e) {
                    // If parsing fails, use the raw value
                    teacherCode = teacherCodeRaw;
                  }
                } else {
                  teacherCode = teacherCodeRaw;
                }
                const teacher = teacherCode ? findStaffByCode(teacherCode) : null;
                // Use display code consistently for assigned teacher
                const displayTeacherCode = teacher ? getStaffDisplayCode(teacher) : teacherCode;
                const cellStatus = getCellStatus(classItem.id, line?.id || line, teacherCodeRaw // ✅ Use the raw value that contains the JSON
                );
                // No `border-r` here: the wrapping <td> owns the column border, so
                // stacked slots don't each draw their own.
                let cellClasses = 'px-2 py-2 text-center cursor-pointer hover:bg-ink-100 relative transition-all duration-200';

                // Check if this class runs on this specific line (via lineAssignments)
                const lineId = line?.id || line;
                const hasLineAssignment = classItem.lineAssignments && lineId in classItem.lineAssignments;

                // Fallback: Check if the subject is available on this line (for grids not using lineAssignments)
                const subjectKey = classItem.subjectCode || classItem.className || classItem.id;
                const isSubjectAvailable = !hasLineAssignment && isSubjectAvailableOnLine(subjectKey, lineId, subjectLineAvailability);

                // Apply base status styling first
                if (cellStatus === 'assigned') {
                  cellClasses += ' bg-ok-bg border-ok-border hover:bg-ok-bg hover:shadow-md';
                } else if (cellStatus === 'conflict') {
                  cellClasses += ' bg-danger-bg border-danger-border hover:bg-danger-bg ring-2 ring-danger-solid';
                } else if (cellStatus === 'incomplete') {
                  cellClasses += ' bg-warn-bg border-warn-border hover:bg-warn-bg ring-2 ring-warn-solid';
                } else {
                  // Apply blue highlighting for cells where the class runs (lineAssignments) OR is available
                  if (hasLineAssignment || isSubjectAvailable) {
                    // Make blue much more visible for scheduled/available slots
                    cellClasses += ' bg-navy-100 border-2 border-navy-300 hover:bg-navy-200';
                  }
                  // Skip gray application - cells are gray by default
                }

                // Apply flag styling LAST so it overrides other styling
                const currentFlag = isCellFlagged(flags, classItem.id, line?.id || line);
                if (currentFlag) {
                  const flagConfig = getFlagColorConfig(currentFlag.color);
                  if (flagConfig) {
                    // Remove previous background and border classes and apply flag styling
                    cellClasses = cellClasses.replace(/bg-\w+-\d+/g, '');
                    cellClasses = cellClasses.replace(/border-\w+-\d+/g, '');
                    cellClasses = cellClasses.replace(/ring-\d+/g, '');
                    cellClasses = cellClasses.replace(/ring-\w+-\d+/g, '');
                    cellClasses = cellClasses.replace(/hover:bg-\w+-\d+/g, '');
                    
                    // Apply flag styling
                    cellClasses += ` ${flagConfig.cellBgClass} ${flagConfig.cellBorderClass} ring-2 ring-offset-1 ${flagConfig.cellBorderClass.replace('border-', 'ring-')} hover:${flagConfig.bgClass}`;
                  }
                }
    return <div key={classItem.id + "-" + (line?.id || line)} className={cellClasses + " flex flex-col items-center justify-center"} style={slotStyle} onClick={() => handleCellClick(classItem.id, line?.id || line)} title={currentFlag ? `${isMultiTeacher ? `Multiple teachers assigned (${teacherAssignments.length})` : displayTeacherCode ? `${displayTeacherCode} - ${teacher?.fullName || 'Unknown Teacher'}` : 'Click to assign teacher'} • FLAGGED: ${currentFlag.note || 'No note'}` : isMultiTeacher ? `Multiple teachers assigned (${teacherAssignments.length})` : displayTeacherCode ? `${displayTeacherCode} - ${teacher?.fullName || 'Unknown Teacher'}` : 'Click to assign teacher'}>
                          {/* Flag indicator - always show if flagged */}
                          {currentFlag && (
                            <div className="absolute top-1 right-1 z-10">
                              <Flag className={`h-3 w-3 ${getFlagColorConfig(currentFlag.color)?.iconClass || 'text-ink-500'}`} />
                            </div>
                          )}
                          {teacherCodeRaw ? <div className="text-xs font-medium text-ink-900">
                              {isMultiTeacher ?
                    // Multi-teacher display
                    <div>
                                  <div className="flex items-center justify-center mb-0.5">
                                    <span className="font-mono tabular-nums text-xs font-medium bg-navy-100 text-navy-800 px-1 py-0.5 rounded-full">
                                      {teacherAssignments.length} Teachers
                                    </span>
                                    {cellStatus === 'conflict' && <div className="ml-1 text-danger-fg">
                                        <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
                                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                        </svg>
                                      </div>}
                                    {cellStatus === 'incomplete' && <div className="ml-1 text-warn-fg">
                                        <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
                                          <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                        </svg>
                                      </div>}
                                  </div>
                                  <div className="text-xs space-y-0.5 mt-0.5">
                                    {teacherAssignments.slice(0, 2).map((assignment, i) => {
                          const teacherInfo = findStaffByCode(assignment.teacherCode);
                          const displayName = teacherInfo?.fullName || teacherInfo?.name || '';
                          const percentage = Math.round(assignment.minutes / (classItem.minutesPerWeek || DEFAULT_MINUTES_PER_LINE) * 100);
                          return <div key={`${assignment.teacherCode}-${i}`} className="flex items-center justify-between">
                                            <div className="flex items-center">
                                              <span className="font-mono tabular-nums font-bold">
                                                {assignment.teacherCode}
                                              </span>
                                              {displayName && <span className="ml-1 text-ink-700 truncate max-w-[60px]" title={displayName}>
                                                  {displayName}
                                                </span>}
                                            </div>
                                            <span className="font-mono tabular-nums text-ink-500 text-xs font-medium">
                                              {percentage}%
                                            </span>
                                          </div>;
                        })}
                                    {teacherAssignments.length > 2 && <div className="font-mono tabular-nums text-center text-ink-500 text-xs">
                                        +{teacherAssignments.length - 2} more
                                      </div>}
                                    {/* Display total percentage for incomplete assignments */}
                                    {cellStatus === 'incomplete' && <div className="font-mono tabular-nums text-xs text-warn-fg font-medium mt-1 pt-1 border-t border-warn-border">
                                        {Math.round(teacherAssignments.reduce((sum, a) => sum + (a.minutes || 0), 0) / (classItem.minutesPerWeek || DEFAULT_MINUTES_PER_LINE) * 100)}
                                        % of required time
                                      </div>}
                                  </div>
                                </div> :
                    // Single teacher display (original code)
                    <div>
                                  <div className="flex items-center justify-center mb-0.5">
                                    <span className="font-mono tabular-nums text-sm font-bold">
                                      {displayTeacherCode}
                                    </span>
                                    {cellStatus === 'conflict' && <div className="ml-1 text-danger-fg">
                                        <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
                                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                        </svg>
                                      </div>}
                                    {cellStatus === 'assigned' && <div className="ml-1 text-ok-fg">
                                        <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
                                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                        </svg>
                                      </div>}
                                  </div>
                                  {teacher && <div className="text-xs text-ink-500 truncate font-medium">
                                      {teacher.fullName || teacher.name}
                                    </div>}
                                </div>}
                            </div> : <div className="text-ink-300 hover:text-navy-500 transition-colors">
                              <span className="text-xl font-light">+</span>
                              <div className="text-xs text-ink-400 mt-0.5 font-medium">
                                Assign
                              </div>
                            </div>}
                        </div>;

  };
  return <div className="h-full flex flex-col">
        {/* Controls Row */}
        <div className="flex flex-wrap gap-2 items-center py-2 px-4 border-b bg-ink-50 w-full">
          {/* Subject search — matches class name, subject code and learning area */}
          <div className="relative">
            <label className="block text-xs font-medium text-ink-700 mb-0.5">
              <Search className="h-4 w-4 inline mr-1" />
              Search
            </label>
            <div className="relative">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search subjects, e.g. Media..."
                className="border border-ink-300 rounded-md pl-8 pr-7 py-1 bg-white text-sm min-w-[220px] focus:outline-none focus:ring-1 focus:ring-navy-500"
              />
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-400" />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  aria-label="Clear subject search"
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-ink-400 hover:text-ink-700"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          </div>

          {/* Curriculum Area Multi-Select Filter */}
          <div className="relative curriculum-area-dropdown">
            <label className="block text-xs font-medium text-ink-700 mb-0.5">
              <Filter className="h-4 w-4 inline mr-1" />
              Learning Areas
            </label>
            <div className="relative">
              <div 
                className="border border-ink-300 rounded-md px-2 py-1 bg-white min-w-[300px] cursor-text"
                onClick={() => setIsAreaDropdownOpen(!isAreaDropdownOpen)}
              >
                <div className="flex flex-wrap gap-1 items-center">
                  {selectedCurriculumAreas.length === 0 ? (
                    <span className="text-ink-500 text-sm">Select learning areas...</span>
                  ) : (
                    selectedCurriculumAreas.map(areaName => (
                      <span key={areaName} className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-navy-100 text-navy-800">
                        {areaName}
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedCurriculumAreas(selectedCurriculumAreas.filter(name => name !== areaName));
                          }}
                          className="ml-1 inline-flex items-center p-0.5 rounded-full text-navy-400 hover:bg-navy-200 hover:text-navy-600"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    ))
                  )}
                  <div className="ml-auto">
                    <svg className="h-4 w-4 text-ink-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>
              </div>
              
              {isAreaDropdownOpen && (
                <div className="absolute z-50 mt-1 w-full bg-white border border-ink-300 rounded-md shadow-lg max-h-60 overflow-auto">
                  <div className="p-2 border-b">
                    <input
                      type="text"
                      placeholder="Search areas..."
                      value={areaSearchQuery}
                      onChange={(e) => setAreaSearchQuery(e.target.value)}
                      className="w-full px-2 py-1 text-sm border border-ink-300 rounded focus:outline-none focus:ring-1 focus:ring-navy-500"
                    />
                  </div>
                  <div className="py-1">
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedCurriculumAreas([]);
                        setIsAreaDropdownOpen(false);
                      }}
                      className="w-full px-3 py-2 text-left text-sm hover:bg-ink-100 flex items-center justify-between"
                    >
                      <span>All Classes</span>
                      <span className="text-ink-500">({grid.classes.length})</span>
                    </button>
                    <div className="border-t border-ink-200"></div>
                    {availableCurriculumAreas
                      .filter(area => area.name.toLowerCase().includes(areaSearchQuery.toLowerCase()))
                      .map(area => {
                        const count = curriculumAreaCounts.get(area.name) || 0;
                        const isSelected = selectedCurriculumAreas.includes(area.name);
                        return (
                          <button
                            key={area.id}
                            type="button"
                            onClick={() => {
                              if (isSelected) {
                                setSelectedCurriculumAreas(selectedCurriculumAreas.filter(name => name !== area.name));
                              } else {
                                setSelectedCurriculumAreas([...selectedCurriculumAreas, area.name]);
                              }
                            }}
                            className={`w-full px-3 py-2 text-left text-sm hover:bg-ink-100 flex items-center justify-between ${
                              isSelected ? 'bg-navy-50 text-navy-900' : ''
                            }`}
                          >
                            <div className="flex items-center">
                              {isSelected && <Check className="h-4 w-4 mr-2 text-navy-600" />}
                              <span className={isSelected ? 'font-medium' : ''}>{area.name}</span>
                            </div>
                            <span className="text-ink-500">({count})</span>
                          </button>
                        );
                      })
                    }
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Level Filter */}
          <div className="relative level-dropdown">
            <label className="block text-xs font-medium text-ink-700 mb-0.5">
              <Filter className="h-4 w-4 inline mr-1" />
              Levels
            </label>
            <div className="relative">
              <div 
                className="border border-ink-300 rounded-md px-2 py-1 bg-white min-w-[250px] cursor-text"
                onClick={() => setIsLevelDropdownOpen(!isLevelDropdownOpen)}
              >
                <div className="flex flex-wrap gap-1 items-center">
                  {selectedLevels.length === 0 ? (
                    <span className="text-ink-500 text-sm">Select levels...</span>
                  ) : (
                    selectedLevels.map(levelName => (
                      <span key={levelName} className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-ok-bg text-ok-fg">
                        {levelName}
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedLevels(selectedLevels.filter(name => name !== levelName));
                          }}
                          className="ml-1 inline-flex items-center p-0.5 rounded-full text-ok-solid hover:bg-ok-bg hover:text-ok-fg"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    ))
                  )}
                  <div className="ml-auto">
                    <svg className="h-4 w-4 text-ink-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>
              </div>
              
              {isLevelDropdownOpen && (
                <div className="absolute z-50 mt-1 w-full bg-white border border-ink-300 rounded-md shadow-lg max-h-60 overflow-auto">
                  <div className="p-2 border-b">
                    <input
                      type="text"
                      placeholder="Search levels..."
                      value={levelSearchQuery}
                      onChange={(e) => setLevelSearchQuery(e.target.value)}
                      className="w-full px-2 py-1 text-sm border border-ink-300 rounded focus:outline-none focus:ring-1 focus:ring-ok-solid"
                    />
                  </div>
                  <div className="py-1">
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedLevels([]);
                        setIsLevelDropdownOpen(false);
                      }}
                      className="w-full px-3 py-2 text-left text-sm hover:bg-ink-100 flex items-center justify-between"
                    >
                      <span>All Levels</span>
                      <span className="text-ink-500">({grid.classes.length})</span>
                    </button>
                    <div className="border-t border-ink-200"></div>
                    {Array.from(levelCounts.keys())
                      .sort((a, b) => {
                        // Sort Year levels numerically, then alphabetically
                        const aYear = a.match(/Year (\d+)/);
                        const bYear = b.match(/Year (\d+)/);
                        if (aYear && bYear) {
                          return parseInt(aYear[1]) - parseInt(bYear[1]);
                        }
                        return a.localeCompare(b);
                      })
                      .filter(level => level.toLowerCase().includes(levelSearchQuery.toLowerCase()))
                      .map(level => {
                        const count = levelCounts.get(level) || 0;
                        const isSelected = selectedLevels.includes(level);
                        
                        // Skip levels with no classes
                        if (count === 0) return null;
                        
                        return (
                          <button
                            key={level}
                            type="button"
                            onClick={() => {
                              if (isSelected) {
                                setSelectedLevels(selectedLevels.filter(name => name !== level));
                              } else {
                                setSelectedLevels([...selectedLevels, level]);
                              }
                            }}
                            className={`w-full px-3 py-2 text-left text-sm hover:bg-ink-100 flex items-center justify-between ${
                              isSelected ? 'bg-ok-bg text-ok-fg' : ''
                            }`}
                          >
                            <div className="flex items-center">
                              {isSelected && <Check className="h-4 w-4 mr-2 text-ok-fg" />}
                              <span className={isSelected ? 'font-medium' : ''}>{level}</span>
                            </div>
                            <span className="text-ink-500">({count})</span>
                          </button>
                        );
                      }).filter(Boolean)
                    }
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Staff Filter */}
          <div className="relative staff-filter-dropdown" data-staff-filter-dropdown>
            <label className="block text-xs font-medium text-ink-700 mb-0.5">
              <Users className="h-4 w-4 inline mr-1" />
              Staff Filter
            </label>
            <div className="relative">
              <div 
                className="border border-ink-300 rounded-md px-2 py-1 bg-white min-w-[250px] cursor-text"
                onClick={() => setShowStaffFilterDropdown(!showStaffFilterDropdown)}
              >
                <div className="flex flex-wrap gap-1 items-center">
                  {staffFilter.length === 0 ? (
                    <span className="text-ink-500 text-sm">Select staff members...</span>
                  ) : (
                    staffFilter.slice(0, 3).map(staffInitial => {
                      const staffMember = findStaffByCode(staffInitial);
                      return (
                        <span key={staffInitial} className="font-mono tabular-nums inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-navy-100 text-navy-800">
                          {staffMember ? getStaffDisplayCode(staffMember) : staffInitial}
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              // Remove this staff member from filter
                              const newFilter = staffFilter.filter(id => id !== staffInitial);
                              onStaffFilterChange?.(newFilter);
                            }}
                            className="ml-1 hover:bg-navy-200 rounded-sm"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </span>
                      );
                    })
                  )}
                  {staffFilter.length > 3 && (
                    <span className="text-xs text-navy-600 bg-navy-50 px-2 py-1 rounded-md">
                      +{staffFilter.length - 3} more
                    </span>
                  )}
                  <ChevronDown className="h-4 w-4 text-ink-400 ml-auto" />
                </div>
              </div>

              {/* Staff Filter Dropdown */}
              {showStaffFilterDropdown && (
                <div className="absolute left-0 mt-1 w-80 bg-white rounded-md shadow-lg border border-ink-200 z-50">
                  <div className="p-3">
                    {/* Search */}
                    <div className="relative mb-3">
                      <input
                        type="text"
                        placeholder="Search staff..."
                        value={staffFilterSearchQuery}
                        onChange={(e) => setStaffFilterSearchQuery(e.target.value)}
                        className="w-full pl-8 pr-3 py-2 text-sm border border-ink-300 rounded-md focus:outline-none focus:ring-1 focus:ring-navy-500"
                      />
                      <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-ink-400" />
                    </div>
                    
                    {/* Clear All / Select All */}
                    <div className="flex justify-between items-center mb-2">
                      <button
                        onClick={() => {
                          onStaffFilterChange?.([]);
                        }}
                        className="text-xs text-navy-600 hover:text-navy-800"
                        disabled={staffFilter.length === 0}
                      >
                        Clear All
                      </button>
                      <button
                        onClick={() => {
                          const allAssignedStaff = assignedStaffMembers.map(s => getStaffDisplayCode(s));
                          onStaffFilterChange?.(allAssignedStaff);
                        }}
                        className="text-xs text-navy-600 hover:text-navy-800"
                      >
                        Select All Assigned
                      </button>
                    </div>
                    
                    {/* Staff List */}
                    <div className="max-h-48 overflow-y-auto space-y-1">
                      {assignedStaffMembers
                        .filter(s => 
                          (staffFilterSearchQuery === '' || 
                           (s.fullName || '').toLowerCase().includes(staffFilterSearchQuery.toLowerCase()) ||
                           (getStaffDisplayCode(s) || '').toLowerCase().includes(staffFilterSearchQuery.toLowerCase()) ||
                           (s.department || '').toLowerCase().includes(staffFilterSearchQuery.toLowerCase()))
                        )
                        .sort((a, b) => a.fullName.localeCompare(b.fullName))
                        .map(staffMember => (
                          <label key={getStaffDisplayCode(staffMember)} className="flex items-center p-2 hover:bg-ink-50 rounded cursor-pointer">
                            <input
                              type="checkbox"
                              checked={staffFilter.includes(getStaffDisplayCode(staffMember))}
                              onChange={(e) => {
                                let newFilter;
                                if (e.target.checked) {
                                  newFilter = [...staffFilter, getStaffDisplayCode(staffMember)];
                                } else {
                                  newFilter = staffFilter.filter(id => id !== getStaffDisplayCode(staffMember));
                                }
                                onStaffFilterChange?.(newFilter);
                              }}
                              className="mr-3 h-4 w-4 text-navy-600 focus:ring-navy-500 border-ink-300 rounded"
                            />
                            <div className="flex-1">
                              <div className="text-sm font-medium text-ink-900">
                                {getStaffDisplayCode(staffMember)} - {staffMember.fullName}
                              </div>
                              <div className="text-xs text-ink-500">
                                {staffMember.department}
                              </div>
                            </div>
                          </label>
                        ))
                      }
                    </div>
                    
                    {/* No results */}
                    {staff.filter(s => 
                      s.status === 'active' && 
                      (staffFilterSearchQuery === '' || 
                       s.fullName?.toLowerCase().includes(staffFilterSearchQuery.toLowerCase()) ||
                       getStaffDisplayCode(s).toLowerCase().includes(staffFilterSearchQuery.toLowerCase()) ||
                       s.department?.toLowerCase().includes(staffFilterSearchQuery.toLowerCase()))
                    ).length === 0 && (
                      <div className="text-center py-4 text-ink-500 text-sm">
                        No staff found matching "{staffFilterSearchQuery}"
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Semester Filter */}
          <div className="relative semester-dropdown">
            <label className="block text-xs font-medium text-ink-700 mb-0.5">
              <Filter className="h-4 w-4 inline mr-1" />
              Semester
            </label>
            <div className="relative">
              <div 
                className="border border-ink-300 rounded-md px-2 py-1 bg-white min-w-[250px] cursor-text"
                onClick={() => setShowSemesterDropdown(!showSemesterDropdown)}
              >
                <div className="flex flex-wrap gap-1 items-center">
                  {selectedSemesters.length === 0 || selectedSemesters.includes('fullyear') ? (
                    <span className="text-ink-500 text-sm">Full Year (all lines)</span>
                  ) : (
                    selectedSemesters.map(semesterId => {
                      const semester = effectiveSemesters.find(s => s.id === semesterId);
                      return (
                        <span key={semesterId} className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-warn-bg text-warn-fg">
                          {semester?.name || semesterId}
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              const newSemesters = selectedSemesters.filter(id => id !== semesterId);
                              setSelectedSemesters(newSemesters.length === 0 ? ['fullyear'] : newSemesters);
                            }}
                            className="ml-1 hover:bg-warn-bg rounded-sm"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </span>
                      );
                    })
                  )}
                  <ChevronDown className="h-4 w-4 text-ink-400 ml-auto" />
                </div>
              </div>

              {/* Semester Dropdown */}
              {showSemesterDropdown && (
                <div className="absolute left-0 mt-1 w-full bg-white rounded-md shadow-lg border border-ink-200 z-50">
                  <div className="p-3">
                    {/* Full Year Option */}
                    <label className="flex items-center p-2 hover:bg-ink-50 rounded cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedSemesters.includes('fullyear')}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedSemesters(['fullyear']);
                          } else {
                            setSelectedSemesters([]);
                          }
                        }}
                        className="mr-3 h-4 w-4 text-navy-600 focus:ring-navy-500 border-ink-300 rounded"
                      />
                      <div className="flex-1">
                        <div className="text-sm font-medium text-ink-900">
                          Full Year
                        </div>
                        <div className="text-xs text-ink-500">
                          All lines ({grid.lines.length} lines)
                        </div>
                      </div>
                    </label>
                    
                    <div className="border-t border-ink-200 my-2"></div>
                    
                    {/* Individual Semesters */}
                    {effectiveSemesters.filter(s => s.id !== 'fullyear').map(semester => (
                      <label key={semester.id} className="flex items-center p-2 hover:bg-ink-50 rounded cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedSemesters.includes(semester.id)}
                          onChange={(e) => {
                            let newSemesters;
                            if (e.target.checked) {
                              // Remove 'fullyear' if selecting specific semesters
                              newSemesters = selectedSemesters.filter(id => id !== 'fullyear');
                              newSemesters.push(semester.id);
                            } else {
                              newSemesters = selectedSemesters.filter(id => id !== semester.id);
                              // If no semesters selected, default to full year
                              if (newSemesters.length === 0) {
                                newSemesters = ['fullyear'];
                              }
                            }
                            setSelectedSemesters(newSemesters);
                          }}
                          className="mr-3 h-4 w-4 text-navy-600 focus:ring-navy-500 border-ink-300 rounded"
                        />
                        <div className="flex-1">
                          <div className="text-sm font-medium text-ink-900">
                            {semester.name}
                          </div>
                          <div className="text-xs text-ink-500">
                            {semester.lineIds?.length || 0} lines
                          </div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Class Filter */}
          <div className="relative class-filter-dropdown">
            <label className="block text-xs font-medium text-ink-700 mb-0.5">
              <Filter className="h-4 w-4 inline mr-1" />
              Classes
            </label>
            <div className="relative">
              <div 
                className="border border-ink-300 rounded-md px-2 py-1 bg-white min-w-[250px] cursor-text"
                onClick={() => setShowClassFilterDropdown(!showClassFilterDropdown)}
              >
                <div className="flex flex-wrap gap-1 items-center">
                  {selectedClasses.length === 0 ? (
                    <span className="text-ink-500 text-sm">Select classes...</span>
                  ) : (
                    selectedClasses.slice(0, 3).map(className => (
                      <span key={className} className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-info-bg text-info-fg">
                        {className.length > 15 ? `${className.substring(0, 15)}...` : className}
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedClasses(selectedClasses.filter(c => c !== className));
                          }}
                          className="ml-1 hover:bg-info-bg rounded-sm"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    ))
                  )}
                  {selectedClasses.length > 3 && (
                    <span className="text-xs text-info-fg bg-info-bg px-2 py-1 rounded-md">
                      +{selectedClasses.length - 3} more
                    </span>
                  )}
                  <ChevronDown className="h-4 w-4 text-ink-400 ml-auto" />
                </div>
              </div>

              {/* Class Filter Dropdown */}
              {showClassFilterDropdown && (
                <div className="absolute left-0 mt-1 w-80 bg-white rounded-md shadow-lg border border-ink-200 z-50">
                  <div className="p-3">
                    {/* Search */}
                    <div className="relative mb-3">
                      <input
                        type="text"
                        placeholder="Search classes..."
                        value={classFilterSearchQuery}
                        onChange={(e) => setClassFilterSearchQuery(e.target.value)}
                        className="w-full pl-8 pr-3 py-2 text-sm border border-ink-300 rounded-md focus:outline-none focus:ring-1 focus:ring-navy-500"
                      />
                      <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-ink-400" />
                    </div>
                    
                    {/* Clear All / Select All */}
                    <div className="flex justify-between items-center mb-2">
                      <button
                        onClick={() => setSelectedClasses([])}
                        className="text-xs text-navy-600 hover:text-navy-800"
                        disabled={selectedClasses.length === 0}
                      >
                        Clear All
                      </button>
                      <button
                        onClick={() => {
                          // Get unique class names to avoid duplicates
                          const allClasses = Array.from(new Set(grid.classes.map(c => c.className)));
                          setSelectedClasses(allClasses);
                        }}
                        className="text-xs text-navy-600 hover:text-navy-800"
                      >
                        Select All Classes
                      </button>
                    </div>
                    
                    {/* Class List */}
                    <div className="max-h-48 overflow-y-auto space-y-1">
                      {grid.classes
                        .filter(classItem => 
                          classFilterSearchQuery === '' || 
                          (classItem.className || '').toLowerCase().includes(classFilterSearchQuery.toLowerCase())
                        )
                        .sort((a, b) => a.className.localeCompare(b.className))
                        .map(classItem => (
                          <label key={classItem.id} className="flex items-center p-2 hover:bg-ink-50 rounded cursor-pointer">
                            <input
                              type="checkbox"
                              checked={selectedClasses.includes(classItem.className)}
                              onChange={(e) => {
                                let newClasses;
                                if (e.target.checked) {
                                  // Add className only if it's not already in the array (prevent duplicates)
                                  if (!selectedClasses.includes(classItem.className)) {
                                    newClasses = [...selectedClasses, classItem.className];
                                  } else {
                                    newClasses = selectedClasses;
                                  }
                                } else {
                                  newClasses = selectedClasses.filter(c => c !== classItem.className);
                                }
                                setSelectedClasses(newClasses);
                              }}
                              className="mr-3 h-4 w-4 text-navy-600 focus:ring-navy-500 border-ink-300 rounded"
                            />
                            <div className="flex-1">
                              <div className="text-sm font-medium text-ink-900 flex items-center gap-1">
                                {classItem.className}
                                {classItem.isCompositeSubClass && (
                                  <span className="px-1.5 py-0.5 text-xs bg-warn-bg text-warn-fg rounded" title="This class is marked as a sub-class of a composite">
                                    Sub-class
                                  </span>
                                )}
                                {classItem.isComposite && (
                                  <span className="px-1.5 py-0.5 text-xs bg-navy-100 text-navy-800 rounded" title="This is a composite class">
                                    Composite
                                  </span>
                                )}
                              </div>
                              <div className="text-xs text-ink-500">
                                {displayLearningArea(classItem.curriculumArea)} • Level: {classItem.level || extractClassLevel(classItem.className)}
                              </div>
                            </div>
                          </label>
                        ))
                      }
                    </div>
                    
                    {/* No results */}
                    {grid.classes.filter(classItem => 
                      classFilterSearchQuery === '' || 
                      (classItem.className || '').toLowerCase().includes(classFilterSearchQuery.toLowerCase())
                    ).length === 0 && (
                      <div className="text-center py-4 text-ink-500 text-sm">
                        No classes found matching "{classFilterSearchQuery}"
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Flag Filter */}
          <div className="relative flag-filter-dropdown">
            <label className="block text-xs font-medium text-ink-700 mb-0.5">
              <Flag className="h-4 w-4 inline mr-1" />
              Flags
            </label>
            <div className="relative">
              <div 
                className="border border-ink-300 rounded-md px-2 py-1 bg-white min-w-[180px] cursor-text"
                onClick={() => setShowFlagFilterDropdown(!showFlagFilterDropdown)}
              >
                <div className="flex items-center justify-between">
                  <span className="text-sm">
                    {selectedFlagFilter === 'all' ? 'All Classes' :
                     selectedFlagFilter === 'flagged' ? 'Flagged Only' :
                     selectedFlagFilter === 'unflagged' ? 'Unflagged Only' :
                     FLAG_COLORS.find(c => c.id === selectedFlagFilter)?.name || selectedFlagFilter}
                  </span>
                  <ChevronDown className="h-4 w-4 text-ink-400" />
                </div>
              </div>

              {/* Flag Filter Dropdown */}
              {showFlagFilterDropdown && (
                <div className="absolute left-0 mt-1 w-full bg-white rounded-md shadow-lg border border-ink-200 z-50">
                  <div className="py-1">
                    {/* All Classes */}
                    <button
                      onClick={() => {
                        setSelectedFlagFilter('all');
                        setShowFlagFilterDropdown(false);
                      }}
                      className={`w-full px-3 py-2 text-left text-sm hover:bg-ink-100 flex items-center justify-between ${
                        selectedFlagFilter === 'all' ? 'bg-ink-50 font-medium' : ''
                      }`}
                    >
                      <span>All Classes</span>
                      <span className="text-ink-500">({grid.classes.length})</span>
                    </button>
                    
                    <div className="border-t border-ink-200"></div>
                    
                    {/* Flagged/Unflagged */}
                    <button
                      onClick={() => {
                        setSelectedFlagFilter('flagged');
                        setShowFlagFilterDropdown(false);
                      }}
                      className={`w-full px-3 py-2 text-left text-sm hover:bg-ink-100 flex items-center justify-between ${
                        selectedFlagFilter === 'flagged' ? 'bg-ink-50 font-medium' : ''
                      }`}
                    >
                      <span className="flex items-center">
                        <Flag className="h-4 w-4 mr-2 text-navy-600" />
                        Flagged Classes
                      </span>
                      <span className="text-ink-500">
                        ({flags.reduce((acc, flag) => {
                          const classIds = new Set(acc);
                          classIds.add(flag.classId);
                          return Array.from(classIds);
                        }, [] as string[]).length})
                      </span>
                    </button>
                    
                    <button
                      onClick={() => {
                        setSelectedFlagFilter('unflagged');
                        setShowFlagFilterDropdown(false);
                      }}
                      className={`w-full px-3 py-2 text-left text-sm hover:bg-ink-100 flex items-center justify-between ${
                        selectedFlagFilter === 'unflagged' ? 'bg-ink-50 font-medium' : ''
                      }`}
                    >
                      <span className="flex items-center">
                        <div className="h-4 w-4 mr-2 border border-ink-300 rounded"></div>
                        Unflagged Classes
                      </span>
                      <span className="text-ink-500">
                        ({grid.classes.length - flags.reduce((acc, flag) => {
                          const classIds = new Set(acc);
                          classIds.add(flag.classId);
                          return Array.from(classIds);
                        }, [] as string[]).length})
                      </span>
                    </button>
                    
                    {flags.length > 0 && (
                      <>
                        <div className="border-t border-ink-200 my-1"></div>
                        <div className="px-3 py-1 text-xs font-medium text-ink-500 uppercase tracking-wider">
                          By Color
                        </div>
                        
                        {FLAG_COLORS.map(color => {
                          const colorFlagCount = flags.filter(flag => flag.color === color.id).length;
                          if (colorFlagCount === 0) return null;
                          
                          return (
                            <button
                              key={color.id}
                              onClick={() => {
                                setSelectedFlagFilter(color.id);
                                setShowFlagFilterDropdown(false);
                              }}
                              className={`w-full px-3 py-2 text-left text-sm hover:bg-ink-100 flex items-center justify-between ${
                                selectedFlagFilter === color.id ? 'bg-ink-50 font-medium' : ''
                              }`}
                            >
                              <span className="flex items-center">
                                <Flag className={`h-4 w-4 mr-2 ${color.iconClass}`} />
                                {color.name} Flags
                              </span>
                              <span className="text-ink-500">({colorFlagCount})</span>
                            </button>
                          );
                        })}
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Class Type Filter */}
          <div className="relative class-type-dropdown">
            <label className="block text-xs font-medium text-ink-700 mb-0.5">
              <Layers className="h-4 w-4 inline mr-1" />
              Class Type
            </label>
            <div className="relative">
              <div
                className="border border-ink-300 rounded-md px-2 py-1 bg-white min-w-[180px] cursor-pointer"
                onClick={() => setShowClassTypeDropdown(!showClassTypeDropdown)}
              >
                <div className="flex items-center justify-between">
                  <span className="text-sm">
                    {selectedClassType === 'all' ? 'All Classes' :
                     selectedClassType === 'regular' ? 'Regular Only' :
                     selectedClassType === 'composite' ? 'Composite Only' :
                     'Sub-classes Only'}
                  </span>
                  <ChevronDown className="h-4 w-4 text-ink-400" />
                </div>
              </div>

              {/* Class Type Dropdown */}
              {showClassTypeDropdown && (
                <div className="absolute left-0 mt-1 w-full bg-white rounded-md shadow-lg border border-ink-200 z-50">
                  <div className="py-1">
                    <button
                      onClick={() => {
                        setSelectedClassType('all');
                        setShowClassTypeDropdown(false);
                      }}
                      className={`w-full px-3 py-2 text-left text-sm hover:bg-ink-100 ${
                        selectedClassType === 'all' ? 'bg-ink-50 font-medium' : ''
                      }`}
                    >
                      All Classes
                    </button>
                    <button
                      onClick={() => {
                        setSelectedClassType('regular');
                        setShowClassTypeDropdown(false);
                      }}
                      className={`w-full px-3 py-2 text-left text-sm hover:bg-ink-100 ${
                        selectedClassType === 'regular' ? 'bg-ink-50 font-medium' : ''
                      }`}
                    >
                      Regular Classes Only
                    </button>
                    <button
                      onClick={() => {
                        setSelectedClassType('composite');
                        setShowClassTypeDropdown(false);
                      }}
                      className={`w-full px-3 py-2 text-left text-sm hover:bg-ink-100 flex items-center ${
                        selectedClassType === 'composite' ? 'bg-ink-50 font-medium' : ''
                      }`}
                    >
                      <Layers className="h-4 w-4 mr-2 text-navy-600" />
                      Composite Classes Only
                    </button>
                    <button
                      onClick={() => {
                        setSelectedClassType('subclass');
                        setShowClassTypeDropdown(false);
                      }}
                      className={`w-full px-3 py-2 text-left text-sm hover:bg-ink-100 ${
                        selectedClassType === 'subclass' ? 'bg-ink-50 font-medium' : ''
                      }`}
                    >
                      Sub-classes Only
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Clear Filters Button */}
          {(selectedCurriculumAreas.length > 0 || selectedLevels.length > 0 || staffFilter.length > 0 || !selectedSemesters.includes('fullyear') || selectedClasses.length > 0 || searchQuery.trim() || selectedFlagFilter !== 'all' || selectedClassType !== 'all') && <button onClick={() => {
        setSelectedCurriculumAreas([]);
        setSelectedLevels([]);
        onStaffFilterChange?.([]);
        setSelectedSemesters(['fullyear']);
        setSelectedClasses([]);
        setSearchQuery('');
        setSelectedFlagFilter('all');
        setSelectedClassType('all');
        setIsAreaDropdownOpen(false);
        setAreaSearchQuery('');
        setIsLevelDropdownOpen(false);
        setLevelSearchQuery('');
        setShowStaffFilterDropdown(false);
        setStaffFilterSearchQuery('');
        setShowSemesterDropdown(false);
        setShowClassFilterDropdown(false);
        setClassFilterSearchQuery('');
        setShowFlagFilterDropdown(false);
        setShowClassTypeDropdown(false);
      }} className="text-xs text-navy-600 hover:text-navy-800 underline">
              Clear Filters
            </button>}

          {/* Create Class Button */}
          <button
            onClick={() => setShowCreateClassModal(true)}
            className="flex items-center gap-2 px-3 py-1.5 bg-ok-solid text-white text-sm font-medium rounded-md hover:bg-ok-solid transition-colors"
          >
            <Plus className="h-4 w-4" />
            Create Class
          </button>

          {/* Create Composite Class Button */}
          {selectedClassIds.length >= 2 && (
            <button
              onClick={() => setShowCompositeModal(true)}
              className="flex items-center gap-2 px-3 py-1.5 bg-navy-600 text-white text-sm font-medium rounded-md hover:bg-navy-700 transition-colors"
            >
              <Layers className="h-4 w-4" />
              Create Composite Class ({selectedClassIds.length})
            </button>
          )}

          {/* Delete Selected Classes Button */}
          {selectedClassIds.length >= 1 && (
            <button
              onClick={handleDeleteSelectedClasses}
              className="flex items-center gap-2 px-3 py-1.5 bg-danger-solid text-white text-sm font-medium rounded-md hover:bg-danger-solid transition-colors"
            >
              <Trash2 className="h-4 w-4" />
              Delete Selected ({selectedClassIds.length})
            </button>
          )}

          {/* Show Sub-classes Toggle */}
          <div className="flex items-center gap-2 px-3 py-1.5 bg-ink-100 rounded-md border border-ink-300">
            <input
              type="checkbox"
              id="show-subclasses"
              checked={showSubClasses}
              onChange={(e) => setShowSubClasses(e.target.checked)}
              className="h-4 w-4 text-navy-600 focus:ring-navy-500 border-ink-300 rounded cursor-pointer"
            />
            <label htmlFor="show-subclasses" className="text-xs font-medium text-ink-700 cursor-pointer select-none">
              Show Sub-classes
            </label>
          </div>

          {/* Group by Subject Toggle */}
          <div className="flex items-center gap-2 px-3 py-1.5 bg-ink-100 rounded-md border border-ink-300">
            <input
              type="checkbox"
              id="group-by-subject"
              checked={groupBySubject}
              onChange={(e) => setGroupBySubject(e.target.checked)}
              className="h-4 w-4 text-navy-600 focus:ring-navy-500 border-ink-300 rounded cursor-pointer"
            />
            <label htmlFor="group-by-subject" className="text-xs font-medium text-ink-700 cursor-pointer select-none">
              Group by Subject
            </label>
          </div>

          {/* Statistics */}
          <div className="text-xs text-ink-500 ml-auto">
            Showing{' '}
            {groupBySubject && displayRows.length !== filteredClasses.length ? (
              <>
                <span className="font-mono tabular-nums">{displayRows.length}</span> subjects
                {' '}(<span className="font-mono tabular-nums">{filteredClasses.length}</span> classes)
              </>
            ) : (
              <><span className="font-mono tabular-nums">{filteredClasses.length}</span> classes</>
            )}
            {' '}• <span className="font-mono tabular-nums">{filteredLines.length}</span>{' '}
            lines
            {showSubClasses && filteredClasses.some(c => c.isCompositeSubClass) && (
              <span className="ml-2 text-ink-500">
                ({filteredClasses.filter(c => c.isCompositeSubClass).length} sub-classes - minutes not counted)
              </span>
            )}
            {conflicts.size > 0 && <span className="ml-2 text-danger-fg">
                • {conflicts.size} conflicts
              </span>}
            {(filteredClasses.length !== grid.classes.filter(c => !c.isCompositeSubClass).length || filteredLines.length !== grid.lines.length) && <span className="ml-2 text-navy-600">
                (filtered from {grid.classes.filter(c => !c.isCompositeSubClass).length} classes,{' '}
                {grid.lines.length} lines)
              </span>}
          </div>
        </div>

        {/* Grid Table - Memoized table structure */}
        <div className="flex-1 overflow-auto w-full">
          <table className="w-full divide-y divide-ink-200">
            <thead className="bg-ink-50 sticky top-0 z-30 shadow-sm">
              <tr>
                {/* Checkbox Column Header */}
                <th className="px-3 py-2 text-center bg-ink-50 z-40 border-r sticky left-0" style={{ width: '50px', minWidth: '50px' }}>
                  <input
                    type="checkbox"
                    checked={selectedClassIds.length > 0 && selectedClassIds.length === filteredClasses.filter(c => !c.isCompositeSubClass).length}
                    onChange={handleSelectAllClasses}
                    className="h-4 w-4 text-navy-600 focus:ring-navy-500 border-ink-300 rounded cursor-pointer"
                    title="Select all classes"
                  />
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-ink-500 uppercase tracking-wider sticky bg-ink-50 z-40 border-r shadow-lg" style={{
              width: '400px',
              minWidth: '400px',
              maxWidth: '400px',
              left: '50px'
            }}>
                  <div className="flex items-center justify-between">
                    <span>{groupBySubject ? 'Subject' : 'Class Name'}</span>
                    <span className="text-xs normal-case text-ink-400 font-mono tabular-nums">
                      {groupBySubject ? `${displayRows.length} rows` : `${filteredClasses.length} classes`}
                    </span>
                  </div>
                </th>
                {filteredLines.map((line, index) => {
              const displayInfo = getLineDisplayInfo(line);
              const availableWidth = typeof window !== 'undefined' ? window.innerWidth - 50 - 400 - 384 : 1200;
              const dynamicWidth = Math.max(180, Math.floor(availableWidth / filteredLines.length));
              return <th key={line?.id || line || Math.random()} className="px-2 py-2 text-center text-xs font-medium text-ink-500 uppercase tracking-wider border-r bg-ink-50 relative group" style={{
                width: `${dynamicWidth}px`,
                minWidth: '180px'
              }}>
                      {editingLineId === (line?.id || line) ?
                // Editing mode
                <div className="space-y-2">
                          <input type="text" value={editingLineName} onChange={e => setEditingLineName(e.target.value)} className="w-full text-center text-sm border border-ink-300 rounded px-1 py-1" autoFocus onKeyDown={e => {
                    if (e.key === 'Enter') handleLineNameSave();
                    if (e.key === 'Escape') handleLineNameCancel();
                  }} />
                          <div className="flex justify-center space-x-1">
                            <button onClick={handleLineNameSave} className="p-1 text-ok-fg hover:bg-ok-bg rounded">
                              <Check className="h-3 w-3" />
                            </button>
                            <button onClick={handleLineNameCancel} className="p-1 text-danger-fg hover:bg-danger-bg rounded">
                              <X className="h-3 w-3" />
                            </button>
                          </div>
                        </div> :
                // Display mode
                <div className="relative">
                          <div className="font-mono tabular-nums font-bold text-lg text-ink-700">
                            {line?.name && line.name !== `Line ${line?.id || line}` && line.name !== (line?.id || line) ? line.name.replace(/Line\s+/i, '').replace(/\s*-\s*Semester\s*[12]/i, '').trim() : (line?.id || line)}
                          </div>
                          <div className={`text-xs normal-case font-medium ${displayInfo.semesterClass}`}>
                            {displayInfo.semester}
                          </div>
                          {line.pairedLineId && selectedSemester === 'fullyear' && <div className="font-mono tabular-nums text-xs text-navy-500 normal-case font-medium mt-1">
                                ↔ {line.pairedLineId}
                              </div>}

                          {onLineUpdate && <button onClick={() => handleLineNameEdit(line?.id || line, line?.name || line?.id || line)} className="absolute -top-1 -right-1 p-1 text-ink-400 hover:text-ink-500 hover:bg-white rounded opacity-0 group-hover:opacity-100 transition-opacity" title="Edit line name">
                              <Edit2 className="h-3 w-3" />
                            </button>}
                        </div>}
                    </th>;
            })}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-ink-200">
              {displayRows.map((row, rowIndex) => {
            const layout = rowLayouts[rowIndex];
            // A group row carries several classes; an ungrouped row has exactly
            // one, so `classItem` still means what it always did below.
            const classItem = row.classes[0];
            const bgColor = getCurriculumColor(row.curriculumArea);
            const selectableIds = row.classes.filter(c => !c.isCompositeSubClass).map(c => c.id);
            const isSelectable = selectableIds.length > 0;
            const selectedInRow = selectableIds.filter(id => selectedClassIds.includes(id)).length;
            const allSelected = isSelectable && selectedInRow === selectableIds.length;
            const someSelected = selectedInRow > 0 && !allSelected;

            // Apply dimmed styling to sub-classes
            const subClassStyle = classItem.isCompositeSubClass ? { opacity: 0.6 } : {};

            return <tr key={row.key} className={`hover:bg-ink-50 ${rowIndex % 2 === 0 ? 'bg-white' : 'bg-ink-50'} ${classItem.isCompositeSubClass ? 'border-l-4 border-l-ink-400' : ''}`} style={{
              ...subClassStyle
            }}>
                    {/* Checkbox Column */}
                    <td className="px-3 py-2 text-center border-r sticky left-0 z-20 align-top" style={{
                      backgroundColor: rowIndex % 2 === 0 ? 'white' : '#FAFAFA',
                      width: '50px',
                      minWidth: '50px'
                    }}>
                      {isSelectable ? (
                        <input
                          type="checkbox"
                          checked={allSelected}
                          ref={el => { if (el) el.indeterminate = someSelected; }}
                          onChange={() => handleToggleRowSelection(selectableIds)}
                          onClick={(e) => e.stopPropagation()}
                          className="h-4 w-4 text-navy-600 focus:ring-navy-500 border-ink-300 rounded cursor-pointer"
                          title={row.isGroup ? `Select all ${selectableIds.length} classes in ${row.label}` : undefined}
                        />
                      ) : (
                        <span className="text-ink-300" title="Sub-classes cannot be selected">-</span>
                      )}
                    </td>
                    {/* Class Name Column - Sticky with increased width */}
                    <td className="px-4 py-2 sticky z-20 border-r shadow-lg align-top" style={{
                backgroundColor: rowIndex % 2 === 0 ? 'white' : '#FAFAFA',
                width: '400px',
                minWidth: '400px',
                left: '50px'
              }}>
                      <div className="flex items-start gap-2">
                        <div className="flex-1">
                          <div className="text-xs font-medium text-ink-900 leading-tight">
                            {row.label}
                          </div>

                          {/* Grouped-row summary: how many classes are folded in,
                              and their learning area. Per-class student counts are
                              omitted because they differ between the classes. */}
                          {row.isGroup && row.classes.length > 1 && (
                            <div className="mt-0.5 flex items-center gap-2 text-xs text-ink-500">
                              <span className="font-mono tabular-nums">
                                {row.classes.length} classes
                              </span>
                              {row.curriculumArea && (
                                <span className="truncate">{displayLearningArea(row.curriculumArea)}</span>
                              )}
                            </div>
                          )}

                          {/* Composite Class Indicator */}
                          {classItem.isComposite && (
                            <div className="mt-1 flex items-center gap-2">
                              <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-navy-100 text-navy-800 border border-navy-300">
                                <Layers className="h-3 w-3 mr-1" />
                                Composite ({classItem.compositeSubClasses?.length || 0} classes)
                              </span>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleBreakApartComposite(classItem.id);
                                }}
                                className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-danger-bg text-danger-fg border border-danger-border hover:bg-danger-bg transition-colors"
                                title="Break apart composite class"
                              >
                                <X className="h-3 w-3 mr-1" />
                                Break Apart
                              </button>
                            </div>
                          )}

                          {/* Sub-Class Indicator */}
                          {classItem.isCompositeSubClass && classItem.parentCompositeId && (
                            <div className="mt-1">
                              <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-ink-100 text-ink-700 border border-ink-300">
                                Part of Composite
                              </span>
                            </div>
                          )}

                          {!row.isGroup && classItem.studentCount && classItem.maxStudents && classItem.studentCount !== classItem.maxStudents && <div className="text-xs text-ink-500 mt-0.5">
                              <span className="font-mono tabular-nums inline-flex items-center px-1 py-0.5 rounded-full bg-navy-100 text-navy-800">
                                {classItem.studentCount}/{classItem.maxStudents}{' '}
                                students
                              </span>
                            </div>}
                        </div>
                      </div>
                    </td>
                    {/* Assignment Columns */}

                    {filteredLines.map(line => {
                const lineId = line?.id || line;
                const occupants = layout.byLine.get(lineId) || [];
                const cellWidth = Math.max(160, (window.innerWidth - 50 - 400 - 384) / filteredLines.length);
                return <td key={lineId || Math.random()} className="p-0 border-r align-top" style={{
                  width: cellWidth + "px",
                  minWidth: '160px'
                }}>
                        <div className="flex flex-col h-full" style={{ minHeight: (layout.maxSlots * ASSIGNMENT_SLOT_MIN_HEIGHT) + "px" }}>
                          {occupants.length === 0 ? (
                            <div className="flex-1" aria-hidden="true" />
                          ) : (
                            occupants.map((occupant, slotIndex) =>
                              renderAssignmentSlot(occupant, line, {
                                flex: 1,
                                minHeight: ASSIGNMENT_SLOT_MIN_HEIGHT + "px",
                                borderTop: slotIndex > 0 ? '1px solid #E4E4E7' : undefined
                              })
                            )
                          )}
                        </div>
                      </td>;
              })}
                  </tr>;
          })}
            </tbody>
          </table>

          {/* Empty State for Filtered Results */}
          {filteredClasses.length === 0 && <EmptyState
              icon={<Search className="h-6 w-6" />}
              title="No classes found"
              description="No classes match your current filters. Try adjusting your search or filters."
              action={<button onClick={() => {
          setSelectedCurriculumAreas([]);
          setSelectedSemester('fullyear');
          setSearchQuery('');
        }} className="text-body-sm text-navy-600 hover:text-navy-800 underline">
                Clear all filters
              </button>}
            />}
        </div>

        {/* Teacher Assignment Modal */}
        {selectedCell && <TeacherAssignmentModal
          classItem={grid.classes.find(c => c.id === selectedCell.classId)!}
          lineId={selectedCell.lineId}
          line={grid.lines.find(l => l.id === selectedCell.lineId)}
          staff={staff}
          onAssign={handleTeacherAssignmentWithDrafting}
          onMultiAssign={handleMultiTeacherAssignment}
          teachingIntentions={teachingIntentions}
          curriculumSubjects={curriculumSubjects}
          preferencesLoading={preferencesLoading}
          onLoadPreferences={onLoadPreferences}
          onCommentsModalStateChange={setIsCommentsModalOpen}
          onFlagCell={onFlagCell}
          onRemoveFlag={onRemoveFlag}
          currentFlag={isCellFlagged(flags, selectedCell.classId, selectedCell.lineId)}
          onClose={() => {
            // Only close if comments modal is not open
            if (!isCommentsModalOpen) {
              setSelectedCell(null);
            }
          }}
        />}

        {/* Line Management Modal */}
        {showLineManagement && onGridUpdate && <LineManagementModal isOpen={showLineManagement} onClose={() => setShowLineManagement(false)} lines={grid.lines} semesters={effectiveSemesters} onSaveChanges={handleLineManagementSave} classCount={grid.classes.length} />}

        {/* Create Composite Class Modal */}
        {showCompositeModal && (
          <CreateCompositeClassModal
            isOpen={showCompositeModal}
            onClose={() => {
              setShowCompositeModal(false);
              setSelectedClassIds([]);
            }}
            selectedClasses={grid.classes.filter(c => selectedClassIds.includes(c.id))}
            onCreateComposite={handleCreateComposite}
          />
        )}

        {/* Create Class Modal */}
        {showCreateClassModal && (
          <CreateClassModal
            isOpen={showCreateClassModal}
            onClose={() => setShowCreateClassModal(false)}
            curriculumAreas={availableCurriculumAreas}
            defaultClassMinutes={grid.defaultClassMinutes}
            onCreateClass={handleCreateClass}
          />
        )}
      </div>;
}, (prevProps, nextProps) => {
  // ✅ FIXED: Custom comparison function to prevent unnecessary re-renders
  // Only re-render if essential props have actually changed
  const gridChanged = prevProps.grid.id !== nextProps.grid.id || prevProps.grid.classes.length !== nextProps.grid.classes.length || prevProps.grid.lines.length !== nextProps.grid.lines.length || JSON.stringify(prevProps.grid.classes.slice(0, 10)) !== JSON.stringify(nextProps.grid.classes.slice(0, 10));
  const staffChanged = prevProps.staff.length !== nextProps.staff.length;
  const handlersChanged = prevProps.onAssignTeacher !== nextProps.onAssignTeacher || prevProps.onClassUpdate !== nextProps.onClassUpdate || prevProps.onGridUpdate !== nextProps.onGridUpdate;
  // If nothing essential changed, don't re-render
  return !gridChanged && !staffChanged && !handlersChanged;
});
StaffAllocationGrid.displayName = 'StaffAllocationGrid';
export default StaffAllocationGrid;